// src/firebase/firebase-delegate.ts
import { analytics, auth, storage } from './firebase'; // Import from firebase-config
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { db } from './firebase'; // Import from firebase-config
import { collection, addDoc, getDocs, doc, updateDoc, deleteDoc, getDoc, setDoc, arrayUnion, arrayRemove, deleteField, Timestamp, query, orderBy, limit, where, QueryConstraint, WhereFilterOp, documentId, writeBatch, startAfter, startAt, endAt, serverTimestamp, increment, QueryDocumentSnapshot, DocumentData, runTransaction } from "firebase/firestore";
import { Appointment, Employee, WorkHours, Customer, CustomerCreateInput, CustomerIdentity, Service, BitStringData, EmployeeInvitation, BusinessConfig, createEmptyBusinessConfig, AppointmentStatus, RecurringBreak, isBlocker, isRealAppointment, isUpcomingStatus, occupiesSchedule, StatsDocNumbers, StatsWrite, emptyStatsAccumulator, PreviewSiteConfig, createEmptyPreviewSiteConfig } from '../types';
import { buildStatsMapsFromAppointments, mergeStatsNumbers, normalizeStatsNumbers, toYyyyMmDdFromMmDdYyyy, toYyyyMmFromYyyyMmDd } from './statsUtils';
import { signOut } from 'firebase/auth';
import { FirebaseError } from 'firebase/app';
import { formatDateToMMDDYYYY, getDatesInRange, getRecurringBreakInstances, isAppointmentStartTimePast, parseDateFromMMDDYYYY, updateScheduleBitString, fillRangeInBitString, RecurringBreakInstance, getAppointmentEndTime, collectAppointmentIdsWithinWindow, isTimeFree, timeToMinutes } from '../time/timeUtils';
import { DAYJS_DAYS_OF_WEEK, APPOINTMENT_ID_LENGTH } from '../constants';
import { logEvent } from 'firebase/analytics';
import { createBookingWebsiteDbRef } from './firebase-references';
import { WEBSITE_ID } from '../projectConfig';
import { dayjs, Dayjs } from '../utils/dayjs-config';
import imageCompression from 'browser-image-compression';
import { customAlphabet } from 'nanoid';
import { callDisableEmployeeLogin } from './cloud-function-helper';

const nanoid = customAlphabet('ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789', APPOINTMENT_ID_LENGTH);

type AppointmentFilter = {
  field: keyof Omit<Appointment, 'id'>;
  operator: WhereFilterOp;
  value: unknown;
};

type EmployeeDeletionGuardReason =
  | 'NOT_FOUND'
  | 'OWNER'
  | 'PRIMARY_ORCHESTRATOR'
  | 'LAST_ADMIN'
  | 'LAST_EMPLOYEE';

export type EmployeeDeletionGuardrail = {
  code: EmployeeDeletionGuardReason;
  message: string;
};

export type EmployeeReassignmentPlan = {
  appointment: Appointment;
  nextEmployee: Employee;
};

export type EmployeeDeletionPreview = {
  targetEmployee: Employee;
  blockersToDelete: Appointment[];
  reassignments: EmployeeReassignmentPlan[];
  unresolvedAppointments: Appointment[];
  guardrailError?: EmployeeDeletionGuardrail;
};

export type ExecuteEmployeeDeletionInput = {
  employeeId: string;
  cancelUnresolved: boolean;
  siteId?: string;
};

export type ExecuteEmployeeDeletionResult = {
  reassignedCount: number;
  cancelledCount: number;
  deletedBlockersCount: number;
  authDisableFailed: boolean;
};

export type EmployeeDeactivationPreview = {
  targetEmployee: Employee;
  reassignments: EmployeeReassignmentPlan[];
  unresolvedAppointments: Appointment[];
};

export type ExecuteEmployeeDeactivationInput = {
  employeeId: string;
  strategy: 'handoff' | 'cancel';
  siteId?: string;
};

export type ExecuteEmployeeDeactivationResult = {
  reassignedCount: number;
  cancelledCount: number;
};

const isNotFoundError = (error: unknown): boolean => {
  return error instanceof FirebaseError && error.code === 'not-found';
};

// Firestore rejects undefined values, so strip them before writes.
const removeUndefinedFields = <T extends object>(source: T): T => {
  const sanitized: Record<string, unknown> = {};
  Object.entries(source as Record<string, unknown>).forEach(([key, value]) => {
    if (value !== undefined) {
      sanitized[key] = value;
    }
  });
  return sanitized as T;
};

const normalizeEmployeeRecord = (employee: Employee): Employee => {
  const legacyHasLogin = (employee as Employee & { hasLogin?: boolean }).hasLogin;
  const isOrchestrated = employee.isOrchestrated ?? (legacyHasLogin === false);
  const normalizedActive = employee.active ?? true;
  const normalizedBookable = employee.bookable ?? true;
  return {
    ...employee,
    isOrchestrated,
    bookable: normalizedBookable,
    active: normalizedActive,
  };
};

export const uploadImageToStorage = async (file: File, path: string, compressionLevel: number = 1): Promise<string> => {
  const storageRef = ref(storage, path);
  let maxSize = 0.8; // 1MB

  switch (compressionLevel) {
    case 1:
      maxSize = 0.8; // 800kb
      break;
    case 2:
      maxSize = 0.3 // 300kb
      break;
    case 3:
      maxSize = 0.1; // 100kb
      break;
    default:
      maxSize = 0.3; // 300kb
      break;
  }

  const compressedFile = await imageCompression(file, {
    maxSizeMB: maxSize,
    useWebWorker: true,
  });
  await uploadBytes(storageRef, compressedFile);
  const downloadURL = await getDownloadURL(storageRef);
  return downloadURL;
};

export const deleteImageFromStorage = async (path: string) => {
  const storageRef = ref(storage, path);
  await deleteObject(storageRef);
  console.log(`Image deleted `);
};

export const getImageFromStorage = async (path: string): Promise<string> => {
  try {
    const storageRef = ref(storage, path); // Create a reference to the path
    const downloadURL = await getDownloadURL(storageRef); // Get the image URL
    return downloadURL;
  } catch (error) {
    console.error("Failed to retrieve image:", error);
    throw error;
  }
};

export const fetchEmployeeById = async (employeeId: string, siteId: string = WEBSITE_ID): Promise<Employee | null> => {
  const db_website_ref: string = createBookingWebsiteDbRef(siteId);
  const employeeDoc = doc(db, db_website_ref, 'employees', employeeId);
  const docSnapshot = await getDoc(employeeDoc);

  console.log("Fetching employee:", employeeId);
  if (docSnapshot.exists()) {
    const employeeData = docSnapshot.data();
    let employee = {
      id: employeeId,
      firstName: employeeData.firstName,
      lastName: employeeData.lastName,
      admin: employeeData.admin,
      appointments: employeeData.appointments,
      phone: employeeData.phone,
      email: employeeData.email,
      profilePictureImagePath: employeeData.profilePictureImagePath,
      bio: employeeData.bio,
      socials: employeeData.socials,
      recurringBreaks: employeeData.recurringBreaks || [],
      bookable: employeeData.bookable,
      isOrchestrated: employeeData.isOrchestrated ?? (employeeData.hasLogin === false),
      active: employeeData.active,
    } as Employee;

    employee = normalizeEmployeeRecord(removeUndefinedFields(employee));
    return employee;
  }
  console.log("No employee found");
  return null;
};

export const fetchAllEmployees = async (siteId: string = WEBSITE_ID): Promise<Employee[]> => {
  const db_website_ref: string = createBookingWebsiteDbRef(siteId);
  const employeesCollection = collection(db, db_website_ref, 'employees');
  const querySnapshot = await getDocs(employeesCollection);

  const employees: Employee[] = [];
  querySnapshot.forEach((doc) => {
    const employeeData = doc.data();
    const employee = normalizeEmployeeRecord(removeUndefinedFields({
      id: doc.id,
      firstName: employeeData.firstName,
      lastName: employeeData.lastName,
      email: employeeData.email,
      phone: employeeData.phone,
      admin: employeeData.admin,
      appointments: employeeData.appointments,
      profilePictureImagePath: employeeData.profilePictureImagePath,
      bio: employeeData.bio,
      socials: employeeData.socials,
      recurringBreaks: employeeData.recurringBreaks || [],
      bookable: employeeData.bookable,
      isOrchestrated: employeeData.isOrchestrated ?? (employeeData.hasLogin === false),
      active: employeeData.active,
    } as Employee));
    employees.push(employee);
  });

  return employees;
};

// Get the current employee data using Firebase Authentication ID
export const addEmployee = async (newEmployee: Employee, siteId: string = WEBSITE_ID): Promise<void> => {
  const { id, ...employeeData } = newEmployee;
  const db_website_ref: string = createBookingWebsiteDbRef(siteId);

  const docRef = doc(db, db_website_ref, 'employees', id);
  const sanitizedEmployeeData = removeUndefinedFields(employeeData);
  await setDoc(docRef, sanitizedEmployeeData);
};

export const updateEmployee = async (
  oldEmployee: Employee,
  updatedEmployee: Employee,
  newProfilePic?: File,
  siteId: string = WEBSITE_ID
): Promise<void> => {
  console.log("Updating employee:", updatedEmployee);
  console.log("Old employee:", oldEmployee);
  const db_website_ref: string = createBookingWebsiteDbRef(siteId);

  // Step 1: Handle image upload and deletion
  if (newProfilePic) {
    let profilePicPath = db_website_ref + "/employees/" + updatedEmployee.id + "/profilePicture";
    // `profilePictures/${updatedEmployee.firstName}_${updatedEmployee.lastName}`;
    // Upload the new profile picture to the new path specified in updatedEmployee
    await uploadImageToStorage(newProfilePic, profilePicPath, /*compressionLevel=*/3);
    console.log("Profile picture uploaded to:", profilePicPath);
  }
  // Step 2: If no new profile picture is provided in updated employee 
  // but the old employee has a profile picture, delete it from storage
  else if (oldEmployee.profilePictureImagePath && !updatedEmployee.profilePictureImagePath) {
    await deleteImageFromStorage(oldEmployee.profilePictureImagePath);
    console.log("Profile picture deleted from:", oldEmployee.profilePictureImagePath);
  }

  // Step 3: Prepare the data to be updated in Firestore
  const employeeData: Record<string, any> = removeUndefinedFields({ ...updatedEmployee });

  // If no new profile picture is provided and there was an old profile picture, delete the profilePictureImagePath field
  if (oldEmployee.profilePictureImagePath && !updatedEmployee.profilePictureImagePath) {
    employeeData.profilePictureImagePath = deleteField(); // Mark the field for deletion
  }

  if (typeof oldEmployee.bio === "string" && !updatedEmployee.bio) {
    employeeData.bio = deleteField();
  }

  console.log("Employee data prepared for update:", employeeData);

  // Step 4: Update the employee document in Firestore
  await updateDoc(doc(db, db_website_ref, "employees", updatedEmployee.id), employeeData);

  // Step 5: Log the update event for analytics
  logEvent(analytics, 'updated_employee', { updatedEmployee: JSON.stringify(updatedEmployee) });
};

export const addEmployeeWorkHours = async (employeeId: string, schedule: WorkHours, siteId: string = WEBSITE_ID): Promise<void> => {
  const db_website_ref: string = createBookingWebsiteDbRef(siteId);
  const docRef = doc(db, db_website_ref, 'workHours', employeeId);
  console.log(docRef.path);
  await setDoc(docRef, schedule);
  logEvent(analytics, 'work_hours_updated', { "employeeId": employeeId, "workHours": JSON.stringify(schedule) });
};

export const updateEmployeeRecurringBreaks = async (
  employeeId: string,
  recurringBreaks: RecurringBreak[],
  siteId: string = WEBSITE_ID
): Promise<void> => {
  const db_website_ref: string = createBookingWebsiteDbRef(siteId);
  const employeeDocRef = doc(db, db_website_ref, 'employees', employeeId);
  const serializedBreaks = recurringBreaks.map((definition) => {
    const base: RecurringBreak = {
      id: definition.id,
      weekday: definition.weekday,
      startTime: definition.startTime,
      endTime: definition.endTime,
      repeatEveryXWeeks: definition.repeatEveryXWeeks,
      indefinite: definition.indefinite ?? false,
    };
    if (!base.indefinite && definition.repeatUntilDate) {
      return { ...base, repeatUntilDate: definition.repeatUntilDate };
    }
    return base;
  });
  await updateDoc(employeeDocRef, { recurringBreaks: serializedBreaks });
  logEvent(analytics, 'updated_recurring_breaks', { employeeId, count: recurringBreaks.length });
};

export const getEmployeeWorkHours = async (employeeId: string, siteId: string = WEBSITE_ID): Promise<WorkHours> => {
  const db_website_ref: string = createBookingWebsiteDbRef(siteId);
  const docRef = doc(db, db_website_ref, 'workHours', employeeId);
  const docSnap = await getDoc(docRef);

  if (docSnap.exists()) {
    return docSnap.data() as WorkHours;
  } else {
    // If no schedule found, you can return null or throw an error based on how you want to handle it
    throw Error("No schedule template found");
  }
};

export const employeeSignOut = async (): Promise<void> => {
  try {
    await signOut(auth);
    logEvent(analytics, 'employee_sign_out', { "employeeId": auth.currentUser?.uid });
    window.location.reload(); // Reload the page to clear the state
  } catch (error) {
    console.error('Error signing out:', error);
    throw new Error('Failed to sign out.');
  }
};

// Get the current employee data using Firebase Authentication ID
export const getCurrentEmployeeId = async (): Promise<string> => {
  const user = auth.currentUser;

  if (user) {
    return user.uid;
  }

  console.log("No authenticated user");
  throw new Error("Error no employee found!");
};

export const getCurrentEmployee = async (): Promise<Employee> => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("No authenticated user");
  }

  try {
    // Get ID token result to access custom claims
    const idTokenResult = await user.getIdTokenResult();
    const userWebsiteId = idTokenResult.claims.websiteId;

    // Ensure the user has the correct websiteId token
    if (!userWebsiteId || userWebsiteId !== WEBSITE_ID) {
      await signOut(auth);
      throw new Error("Unauthorized access: Incorrect website.");
    }

    const employeeId = user.uid;
    const employee = await fetchEmployeeById(employeeId);

    if (!employee) {
      throw new Error("Employee not found");
    }

    return employee;
  } catch (error) {
    console.error("Error fetching employee:", error);
    await signOut(auth); // Ensure sign out on any error
    throw new Error("Failed to fetch employee data");
  }
};

export async function getStripeAccount(employeeId: string) {
  try {
    const ref = doc(db, `booking-websites/${WEBSITE_ID}/employeeStripeAccounts/${employeeId}`);
    const snap = await getDoc(ref);

    if (!snap.exists()) return null;


    return snap.data(); // Contains { stripe_account_id, connectedAt. }
  } catch (error) {
    console.error("Failed to fetch Stripe account:", error);
    return null;
  }
}

const isAppointmentAtOrAfterNow = (appointment: Appointment): boolean => {
  const now = dayjs();
  const combinedMoment = dayjs(
    `${appointment.appointmentDate ?? ''}${appointment.appointmentTime ?? ''}`,
    'MMDDYYYYHHmm',
    true,
  );
  if (combinedMoment.isValid()) {
    return !combinedMoment.isBefore(now);
  }

  const dateOnlyMoment = dayjs(appointment.appointmentDate ?? '', 'MMDDYYYY', true);
  if (dateOnlyMoment.isValid()) {
    return !dateOnlyMoment.endOf('day').isBefore(now);
  }

  // Fall back to legacy behavior for malformed legacy records.
  return true;
};

const realUpcomingForDeletion = (appointment: Appointment): boolean => (
  occupiesSchedule(appointment) &&
  !isBlocker(appointment) &&
  isAppointmentAtOrAfterNow(appointment)
);

const getDeletionGuardrailError = (
  targetEmployee: Employee,
  allEmployees: Employee[],
  businessConfig: BusinessConfig,
): EmployeeDeletionGuardrail | undefined => {
  const ownerId = businessConfig.paymentInfo?.owner?.trim() || '';
  if (ownerId && targetEmployee.id === ownerId) {
    return { code: 'OWNER', message: 'Cannot delete the business owner.' };
  }

  const primaryOrchestratorId = businessConfig.preferences?.primaryOrchestratorId?.trim() || '';
  if (primaryOrchestratorId && targetEmployee.id === primaryOrchestratorId) {
    return { code: 'PRIMARY_ORCHESTRATOR', message: 'Cannot delete the primary orchestrator.' };
  }

  const remainingEmployees = allEmployees.filter((employee) => employee.id !== targetEmployee.id);
  if (remainingEmployees.length === 0) {
    return { code: 'LAST_EMPLOYEE', message: 'Cannot delete the last employee.' };
  }

  if (targetEmployee.admin) {
    const remainingAdmins = remainingEmployees.filter((employee) => employee.admin);
    if (remainingAdmins.length === 0) {
      return { code: 'LAST_ADMIN', message: 'Cannot delete the last admin.' };
    }
  }

  return undefined;
};

const rankCandidateEmployees = (employees: Employee[]): Employee[] => (
  [...employees].sort((left, right) => {
    const leftName = `${left.firstName} ${left.lastName}`.trim().toLowerCase();
    const rightName = `${right.firstName} ${right.lastName}`.trim().toLowerCase();
    const byName = leftName.localeCompare(rightName);
    if (byName !== 0) return byName;
    return left.id.localeCompare(right.id);
  })
);

const getServiceEligibilityMap = (services: Service[]): Map<string, Set<string>> => {
  const byServiceId = new Map<string, Set<string>>();
  services.forEach((service) => {
    byServiceId.set(service.id, new Set(service.employees ?? []));
  });
  return byServiceId;
};

const appointmentReassignmentComparator = (left: Appointment, right: Appointment): number => {
  const leftDate = dayjs(left.appointmentDate, 'MMDDYYYY');
  const rightDate = dayjs(right.appointmentDate, 'MMDDYYYY');
  if (leftDate.isBefore(rightDate)) return -1;
  if (leftDate.isAfter(rightDate)) return 1;
  return Number(left.appointmentTime) - Number(right.appointmentTime);
};

const cleanupEmployeeCustomerLinks = async (
  employeeId: string,
  siteId: string = WEBSITE_ID,
): Promise<void> => {
  const dbWebsiteRef = createBookingWebsiteDbRef(siteId);
  const customersRef = collection(db, dbWebsiteRef, 'customers');
  const customersSnapshot = await getDocs(query(customersRef, where('employeeIds', 'array-contains', employeeId)));

  if (customersSnapshot.empty) {
    return;
  }

  const batches = chunk(customersSnapshot.docs, 400);
  for (const docsBatch of batches) {
    const batch = writeBatch(db);
    docsBatch.forEach((docSnap) => {
      batch.update(docSnap.ref, {
        employeeIds: arrayRemove(employeeId),
      });
    });
    await batch.commit();
  }
};

const cleanupEmployeeServiceLinks = async (
  employeeId: string,
  siteId: string = WEBSITE_ID,
): Promise<void> => {
  const services = await fetchServices(siteId);
  const affectedServices = services.filter((service) => (service.employees ?? []).includes(employeeId));
  if (affectedServices.length === 0) {
    return;
  }

  const dbWebsiteRef = createBookingWebsiteDbRef(siteId);
  const batches = chunk(affectedServices, 400);
  for (const serviceBatch of batches) {
    const batch = writeBatch(db);
    serviceBatch.forEach((service) => {
      batch.update(doc(db, dbWebsiteRef, 'services', service.id), {
        employees: arrayRemove(employeeId),
      });
    });
    await batch.commit();
  }
};

export const previewEmployeeDeletion = async (
  employeeId: string,
  siteId: string = WEBSITE_ID
): Promise<EmployeeDeletionPreview> => {
  const [targetEmployee, allEmployees, businessConfig, services] = await Promise.all([
    fetchEmployeeById(employeeId, siteId),
    fetchAllEmployees(siteId),
    getCurrentBusinessConfig(siteId),
    fetchServices(siteId),
  ]);

  if (!targetEmployee) {
    const notFoundError = { code: 'NOT_FOUND', message: 'Employee not found.' } as EmployeeDeletionGuardrail;
    throw Object.assign(new Error(notFoundError.message), { guardrailError: notFoundError });
  }

  const guardrailError = getDeletionGuardrailError(targetEmployee, allEmployees, businessConfig);
  if (guardrailError) {
    return {
      targetEmployee,
      blockersToDelete: [],
      reassignments: [],
      unresolvedAppointments: [],
      guardrailError,
    };
  }

  const appointmentIds = Array.from(
    new Set(
      Object.values(targetEmployee.appointments ?? {})
        .flat()
        .filter((id): id is string => typeof id === 'string' && id.trim().length > 0)
    )
  );
  const appointments = await getAppointmentsByIds(appointmentIds, siteId, 'previewEmployeeDeletion');

  const blockersToDelete = appointments.filter(isBlocker);
  const reassignableAppointments = appointments
    .filter(realUpcomingForDeletion)
    .sort(appointmentReassignmentComparator);

  const candidateEmployees = rankCandidateEmployees(
    allEmployees.filter((employee) => employee.id !== targetEmployee.id && employee.active !== false)
  );
  const serviceEligibilityByService = getServiceEligibilityMap(services);
  const scheduleCache = new Map<string, string>();
  const plannedReassignments: EmployeeReassignmentPlan[] = [];
  const unresolvedAppointments: Appointment[] = [];

  for (const appointment of reassignableAppointments) {
    const requiredServiceIds = (appointment.servicesList ?? [])
      .map((service) => service.id)
      .filter((id): id is string => typeof id === 'string' && id.trim().length > 0);
    let selectedEmployee: Employee | null = null;

    for (const candidate of candidateEmployees) {
      const hasAllServices = requiredServiceIds.every((serviceId) => {
        const eligibleSet = serviceEligibilityByService.get(serviceId);
        // If the service no longer exists, we avoid blocking reassignment on that mismatch.
        if (!eligibleSet) return true;
        return eligibleSet.has(candidate.id);
      });
      if (!hasAllServices) {
        continue;
      }

      const scheduleKey = `${candidate.id}:${appointment.appointmentDate}`;
      let dayBitString = scheduleCache.get(scheduleKey);
      if (!dayBitString) {
        dayBitString = await fetchEmployeeScheduleDay(candidate.id, appointment.appointmentDate, siteId) ?? '';
      }
      if (!dayBitString) {
        continue;
      }

      const startMinutes = timeToMinutes(appointment.appointmentTime);
      const isAvailable = isTimeFree(dayBitString, startMinutes, appointment.appointmentDuration);
      if (!isAvailable) {
        continue;
      }

      const updatedBitString = updateScheduleBitString(
        dayBitString,
        appointment.appointmentTime,
        appointment.appointmentDuration,
        false,
      );
      if (!updatedBitString) {
        continue;
      }

      scheduleCache.set(scheduleKey, updatedBitString);
      selectedEmployee = candidate;
      break;
    }

    if (!selectedEmployee) {
      unresolvedAppointments.push(appointment);
      continue;
    }

    plannedReassignments.push({
      appointment,
      nextEmployee: selectedEmployee,
    });
  }

  return {
    targetEmployee,
    blockersToDelete,
    reassignments: plannedReassignments,
    unresolvedAppointments,
  };
};

export const executeEmployeeDeletion = async (
  input: ExecuteEmployeeDeletionInput
): Promise<ExecuteEmployeeDeletionResult> => {
  const { employeeId, cancelUnresolved, siteId = WEBSITE_ID } = input;
  const deletionLogPrefix = `[executeEmployeeDeletion][site:${siteId}][employee:${employeeId}]`;
  const runStep = async <T>(step: string, operation: () => Promise<T>): Promise<T> => {
    try {
      return await operation();
    } catch (error) {
      console.error(`${deletionLogPrefix} Step failed: ${step}`, error);
      throw error;
    }
  };
  const preview = await previewEmployeeDeletion(employeeId, siteId);

  if (preview.guardrailError) {
    const error = new Error(preview.guardrailError.message);
    throw Object.assign(error, { guardrailError: preview.guardrailError });
  }

  if (preview.unresolvedAppointments.length > 0 && !cancelUnresolved) {
    const error = new Error('Some appointments cannot be reassigned without cancellation.');
    throw Object.assign(error, { unresolvedAppointments: preview.unresolvedAppointments });
  }

  await runStep('delete blocker appointments', async () => {
    for (const blocker of preview.blockersToDelete) {
      await deleteAppointment(blocker, siteId);
    }
  });

  await runStep('reassign upcoming appointments', async () => {
    for (const reassignment of preview.reassignments) {
      const originalAppointment = reassignment.appointment;
      const nextEmployee = reassignment.nextEmployee;
      const updatedAppointment: Appointment = {
        ...originalAppointment,
        employeeID: nextEmployee.id,
        employeeName: `${nextEmployee.firstName} ${nextEmployee.lastName}`.trim(),
        employeeEmail: nextEmployee.email || '',
        employeePhoneNumber: nextEmployee.phone || '',
      };

      await updateAppointment(originalAppointment, updatedAppointment, originalAppointment.employeeID, undefined, siteId);
      await updateAppointmentInEmployeeProfile(originalAppointment, updatedAppointment, siteId);
    }
  });

  if (cancelUnresolved) {
    await runStep('cancel unresolved appointments', async () => {
      for (const appointment of preview.unresolvedAppointments) {
        await cancelAppointment(appointment, appointment.employeeID, undefined, siteId);
      }
    });
  }

  await runStep('cleanup employee links in services/customers', async () => {
    await cleanupEmployeeServiceLinks(employeeId, siteId);
    await cleanupEmployeeCustomerLinks(employeeId, siteId);
  });

  const dbWebsiteRef = createBookingWebsiteDbRef(siteId);
  const profileImagePath = preview.targetEmployee.profilePictureImagePath;
  if (profileImagePath) {
    try {
      await deleteImageFromStorage(profileImagePath);
    } catch (error) {
      console.warn('Failed to delete employee profile image', error);
    }
  }

  await runStep('delete employee subcollections and linked docs', async () => {
    await Promise.all([
      deleteCollectionPaginated(collection(db, dbWebsiteRef, 'employees', employeeId, 'statsDaily')),
      deleteCollectionPaginated(collection(db, dbWebsiteRef, 'employees', employeeId, 'statsMonthly')),
      deleteDoc(doc(db, dbWebsiteRef, 'workHours', employeeId)).catch(() => undefined),
      deleteDoc(doc(db, dbWebsiteRef, 'schedules', employeeId)).catch(() => undefined),
      deleteDoc(doc(db, dbWebsiteRef, 'employeeStripeAccounts', employeeId)).catch(() => undefined),
    ]);
  });

  // Disable auth after cleanup, but do not block deletion on transient callable/auth failures.
  let authDisableFailed = false;
  if (preview.targetEmployee.isOrchestrated !== true) {
    try {
      await callDisableEmployeeLogin(siteId, employeeId);
    } catch (error) {
      authDisableFailed = true;
      console.warn(`${deletionLogPrefix} Failed to disable employee login before deletion`, error);
    }
  }

  await runStep('delete employee document', async () => {
    await deleteDoc(doc(db, dbWebsiteRef, 'employees', employeeId));
  });

  return {
    reassignedCount: preview.reassignments.length,
    cancelledCount: cancelUnresolved ? preview.unresolvedAppointments.length : 0,
    deletedBlockersCount: preview.blockersToDelete.length,
    authDisableFailed,
  };
};

export const previewEmployeeDeactivation = async (
  employeeId: string,
  siteId: string = WEBSITE_ID,
): Promise<EmployeeDeactivationPreview> => {
  const [targetEmployee, allEmployees, services] = await Promise.all([
    fetchEmployeeById(employeeId, siteId),
    fetchAllEmployees(siteId),
    fetchServices(siteId),
  ]);

  if (!targetEmployee) {
    throw new Error('Employee not found.');
  }

  const appointmentIds = Array.from(
    new Set(
      Object.values(targetEmployee.appointments ?? {})
        .flat()
        .filter((id): id is string => typeof id === 'string' && id.trim().length > 0)
    )
  );
  const appointments = await getAppointmentsByIds(appointmentIds, siteId, 'previewEmployeeDeactivation');
  const reassignableAppointments = appointments
    .filter(realUpcomingForDeletion)
    .sort(appointmentReassignmentComparator);

  const candidateEmployees = rankCandidateEmployees(
    allEmployees.filter((employee) => employee.id !== targetEmployee.id && employee.active !== false)
  );
  const serviceEligibilityByService = getServiceEligibilityMap(services);
  const scheduleCache = new Map<string, string>();
  const plannedReassignments: EmployeeReassignmentPlan[] = [];
  const unresolvedAppointments: Appointment[] = [];

  for (const appointment of reassignableAppointments) {
    const requiredServiceIds = (appointment.servicesList ?? [])
      .map((service) => service.id)
      .filter((id): id is string => typeof id === 'string' && id.trim().length > 0);
    let selectedEmployee: Employee | null = null;

    for (const candidate of candidateEmployees) {
      const hasAllServices = requiredServiceIds.every((serviceId) => {
        const eligibleSet = serviceEligibilityByService.get(serviceId);
        if (!eligibleSet) return true;
        return eligibleSet.has(candidate.id);
      });
      if (!hasAllServices) {
        continue;
      }

      const scheduleKey = `${candidate.id}:${appointment.appointmentDate}`;
      let dayBitString = scheduleCache.get(scheduleKey);
      if (!dayBitString) {
        dayBitString = await fetchEmployeeScheduleDay(candidate.id, appointment.appointmentDate, siteId) ?? '';
      }
      if (!dayBitString) {
        continue;
      }

      const startMinutes = timeToMinutes(appointment.appointmentTime);
      const isAvailable = isTimeFree(dayBitString, startMinutes, appointment.appointmentDuration);
      if (!isAvailable) {
        continue;
      }

      const updatedBitString = updateScheduleBitString(
        dayBitString,
        appointment.appointmentTime,
        appointment.appointmentDuration,
        false,
      );
      if (!updatedBitString) {
        continue;
      }

      scheduleCache.set(scheduleKey, updatedBitString);
      selectedEmployee = candidate;
      break;
    }

    if (!selectedEmployee) {
      unresolvedAppointments.push(appointment);
      continue;
    }

    plannedReassignments.push({
      appointment,
      nextEmployee: selectedEmployee,
    });
  }

  return {
    targetEmployee,
    reassignments: plannedReassignments,
    unresolvedAppointments,
  };
};

export const executeEmployeeDeactivation = async (
  input: ExecuteEmployeeDeactivationInput,
): Promise<ExecuteEmployeeDeactivationResult> => {
  const { employeeId, strategy, siteId = WEBSITE_ID } = input;
  const preview = await previewEmployeeDeactivation(employeeId, siteId);
  const shouldCancelAll = strategy === 'cancel';
  const shouldHandoffAll = strategy === 'handoff';

  if (shouldHandoffAll && preview.unresolvedAppointments.length > 0) {
    throw new Error('Some appointments cannot be handed off. Choose cancellation or resolve them first.');
  }

  if (shouldCancelAll) {
    const allUpcomingAppointments = [
      ...preview.reassignments.map((entry) => entry.appointment),
      ...preview.unresolvedAppointments,
    ];
    for (const appointment of allUpcomingAppointments) {
      await cancelAppointment(appointment, appointment.employeeID, undefined, siteId);
    }
  } else {
    for (const reassignment of preview.reassignments) {
      const originalAppointment = reassignment.appointment;
      const nextEmployee = reassignment.nextEmployee;
      const updatedAppointment: Appointment = {
        ...originalAppointment,
        employeeID: nextEmployee.id,
        employeeName: `${nextEmployee.firstName} ${nextEmployee.lastName}`.trim(),
        employeeEmail: nextEmployee.email || '',
        employeePhoneNumber: nextEmployee.phone || '',
      };

      await updateAppointment(originalAppointment, updatedAppointment, originalAppointment.employeeID, undefined, siteId);
      await updateAppointmentInEmployeeProfile(originalAppointment, updatedAppointment, siteId);
    }
  }

  const dbWebsiteRef = createBookingWebsiteDbRef(siteId);
  await updateDoc(doc(db, dbWebsiteRef, 'employees', employeeId), {
    active: false,
    bookable: false,
  });

  return {
    reassignedCount: shouldCancelAll ? 0 : preview.reassignments.length,
    cancelledCount: shouldCancelAll
      ? preview.reassignments.length + preview.unresolvedAppointments.length
      : 0,
  };
};

/**
 * Fetch a single appointment by ID from Firestore.
 * @param appointmentId - The ID of the appointment to fetch.
 * @returns The appointment object with the given ID.
 */
export const getAppointmentById = async (appointmentId: string, siteId: string = WEBSITE_ID): Promise<Appointment> => {
  const db_website_ref: string = createBookingWebsiteDbRef(siteId);

  try {
    const docRef = doc(db, db_website_ref, 'appointments', appointmentId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      const notFoundError = new FirebaseError(
        'not-found',
        `Appointment with ID ${appointmentId} not found`
      );
      throw notFoundError;
    }

    const data = docSnap.data() as Omit<Appointment, 'id'>;
    return { id: docSnap.id, ...data };
  } catch (error) {
    console.error(`Error fetching appointment by ID (${appointmentId}):`, error);
    throw error;
  }
};

// Fetch all appointments from Firestore
// TODO is this doing too much in one call?
export const getAppointments = async (
  filters: AppointmentFilter[] = [],
  includeBlockers: boolean = false,
  siteId: string = WEBSITE_ID,
): Promise<Appointment[]> => {
  const db_website_ref: string = createBookingWebsiteDbRef(siteId);

  try {
    const appointmentsRef = collection(db, db_website_ref, "appointments");
    const constraints: QueryConstraint[] = filters.map(({ field, operator, value }) =>
      where(field as string, operator, value)
    );
    const snapshotTarget = constraints.length ? query(appointmentsRef, ...constraints) : appointmentsRef;
    const querySnapshot = await getDocs(snapshotTarget);

    const records = querySnapshot.docs.map((docSnapshot) => {
      const data = docSnapshot.data() as Omit<Appointment, 'id'>;
      return { id: docSnapshot.id, ...data };
    });

    return includeBlockers ? records : records.filter(isRealAppointment);
  } catch (error) {
    console.error("Error fetching appointments:", error);
    throw new Error("Failed to fetch appointments"); // Rethrow with a user-friendly message
  }
};

// Fetch active appointments for a specific employee on a given date
export const getActiveAppointments = async (
  employeeId: string,
  date: Dayjs,
  onlyUpcoming: boolean = true,
  includeBlockers: boolean = false,
  siteId: string = WEBSITE_ID,
): Promise<Appointment[]> => {
  const db_website_ref: string = createBookingWebsiteDbRef(siteId);

  try {
    const formattedDate = date.format("MMDDYYYY");

    // Reference to the employee's document
    const employeeDocRef = doc(db, db_website_ref, "employees", employeeId);

    // Fetch the document
    const docSnapshot = await getDoc(employeeDocRef);

    if (!docSnapshot.exists()) {
      return [];
    }

    // Extract the employee data
    const employeeData = docSnapshot.data();

    if (!employeeData?.appointments || typeof employeeData.appointments !== "object") {
      console.log("Appointments object does not exist or is invalid");
      // TODO do we want to throw an error?
      return [];
    }

    // Get the appointment IDs for the specific date
    const appointmentIds = employeeData.appointments[formattedDate] || [];

    if (appointmentIds.length === 0) {
      console.log("No appointments for the given date");
      return [];
    }

    // Fetch the full appointment details for each appointment ID
    const appointmentPromises = appointmentIds.map(async (id: string) => {
      const appointmentDocRef = doc(db, db_website_ref, "appointments", id);
      const appointmentDoc = await getDoc(appointmentDocRef);

      if (!appointmentDoc.exists()) {
        console.log(`Appointment with ID ${id} does not exist`);
        return null;
      }

      return { id, ...appointmentDoc.data() } as Appointment;
    });

    // Wait for all appointment details to be fetched and filter out nulls and COMPLETED ones
    let activeAppointments = (await Promise.all(appointmentPromises)).filter(
      (appointment): appointment is Appointment => appointment !== null
    );

    if (onlyUpcoming) {
      activeAppointments = activeAppointments.filter((appointment) => isUpcomingStatus(appointment.status));
    }

    if (!includeBlockers) {
      activeAppointments = activeAppointments.filter(isRealAppointment);
    }

    console.log("Active appointments:", activeAppointments);


    return activeAppointments;
  } catch (error) {
    console.error("Error fetching active appointments:", error);
    throw new Error("Failed to fetch active appointments");
  }
};

// Fetch active appointments for a specific employee between two dates
export const getActiveAppointmentsBetweenTwoDates = async (
  employeeId: string,
  startDate: Dayjs,
  endDate: Dayjs,
  includeBlockers: boolean = false,
  siteId: string = WEBSITE_ID,
): Promise<Appointment[]> => {
  try {
    if (endDate.isBefore(startDate)) {
      throw new Error("End date cannot be before start date");
    }

    const appointments: Appointment[] = [];
    const currentDate = startDate.clone();

    while (!currentDate.isAfter(endDate)) {
      const dailyAppointments = await getActiveAppointments(
        employeeId,
        currentDate,
        /*onlyUpcoming=*/true,
        includeBlockers,
        siteId,
      );
      appointments.push(...dailyAppointments);

      // Move to the next date
      currentDate.add(1, "day");
    }

    return appointments;
  } catch (error) {
    console.error("Error fetching active appointments between two dates:", error);
    throw new Error("Failed to fetch active appointments between two dates");
  }
};

// Given two appointments it will cancel the first one and book the second one.
export const updateAppointment = async (
  oldAppointment: Appointment,
  newAppointment: Partial<Appointment>,
  lastUpdater: string = oldAppointment.employeeID,
  scheduleBitStrings?: Map<string, string>,
  siteId: string = WEBSITE_ID,
): Promise<void> => {
  const db_website_ref: string = createBookingWebsiteDbRef(siteId);
  const updateScheduleFromCache = async (
    employeeId: string,
    date: string,
    time: string,
    duration: number,
    cancel: boolean,
  ): Promise<boolean> => {
    if (!scheduleBitStrings) {
      return addBookingToSchedule(employeeId, date, time, duration, cancel, /*scheduleBitStrings=*/undefined, siteId);
    }
    const cached = scheduleBitStrings.get(date);
    if (!cached) {
      return addBookingToSchedule(employeeId, date, time, duration, cancel, scheduleBitStrings, siteId);
    }
    const updated = updateScheduleBitString(cached, time, duration, cancel);
    if (!updated) {
      return false;
    }
    const scheduleRef = doc(db, db_website_ref, 'schedules', employeeId);
    await updateDoc(scheduleRef, { [date]: updated });
    scheduleBitStrings.set(date, updated);
    return true;
  };
  const { id: newId, appointmentDate: newDate, appointmentTime: newTime, appointmentDuration } = newAppointment;
  const oldDate = oldAppointment.appointmentDate;
  const oldTime = oldAppointment.appointmentTime;
  const prevEmployeeId: string | undefined = oldAppointment.employeeID;
  const newEmployeeId: string | undefined = newAppointment.employeeID;

  if (!newId) {
    throw new Error('Appointment ID is required for updating.');
  }
  if (!newEmployeeId || !prevEmployeeId) {
    throw new Error('Employee ID is required for updating.');
  }

  const nextDate = newDate ?? oldDate;
  const nextTime = newTime ?? oldTime;
  const nextDuration = appointmentDuration ?? oldAppointment.appointmentDuration;
  const nextEmployeeId = newEmployeeId ?? prevEmployeeId;

  const scheduleChangeNeeded =
    nextDate !== oldDate ||
    nextTime !== oldTime ||
    nextDuration !== oldAppointment.appointmentDuration ||
    nextEmployeeId !== prevEmployeeId;

  let freedOldSlot = false;
  let bookedNewSlot = false;

  try {
    if (scheduleChangeNeeded) {
      // Step 1: Free up the old time slot in the schedule
      freedOldSlot = await updateScheduleFromCache(
        prevEmployeeId,
        oldDate,
        oldTime,
        oldAppointment.appointmentDuration,
        true,
      );
      if (!freedOldSlot) {
        throw new Error('Failed to free up the old time slot. Manual intervention may be required.');
      }

      // Step 2: Book the new time slot in the schedule
      bookedNewSlot = await updateScheduleFromCache(
        nextEmployeeId,
        nextDate,
        nextTime,
        nextDuration,
        false,
      );
      if (!bookedNewSlot) {
        throw new Error('Failed to book the new time slot.');
      }
    }

    // Step 3: Update the appointment in Firestore
    const { id: _, ...updatedFields } = newAppointment; // Exclude the id
    // Ensure lastUpdater is recorded
    updatedFields.lastUpdater = lastUpdater;
    const appointmentDoc = doc(db, db_website_ref, "appointments", newId);
    await updateDoc(appointmentDoc, updatedFields);

    logEvent(analytics, 'updated_appointment', { "oldAppointment": JSON.stringify(oldAppointment), "newAppointment": JSON.stringify(newAppointment) });

    console.log(`Appointment ${newId} updated successfully.`);
  } catch (error) {
    if (scheduleChangeNeeded) {
      try {
        if (bookedNewSlot) {
          await updateScheduleFromCache(
            nextEmployeeId,
            nextDate,
            nextTime,
            nextDuration,
            true // release the new slot
          );
        }
        if (freedOldSlot) {
          await updateScheduleFromCache(
            prevEmployeeId,
            oldDate,
            oldTime,
            oldAppointment.appointmentDuration,
            false // re-book the original slot
          );
        }
      } catch (recoveryError) {
        console.error('Failed to revert schedule changes after update error:', recoveryError);
      }
    }
    console.error(`Failed to update appointment ${newId} : `, error);
    throw new Error(`Failed to update the appointment ${newId}`);
  }
};

// Function to update employee by ID
export const updateEmployeeById = async (employeeId: string, updatedEmployee: Employee, siteId: string = WEBSITE_ID) => {
  const db_website_ref: string = createBookingWebsiteDbRef(siteId);

  const employeeDoc = doc(db, db_website_ref, 'employees', employeeId);
  try {
    const sanitizedEmployee = removeUndefinedFields(updatedEmployee);
    await setDoc(employeeDoc, sanitizedEmployee);
    logEvent(analytics, 'updated_employee', { "updatedEmployee": JSON.stringify(updatedEmployee) });
  } catch (error) {
    console.error("Error updating employee:", error);
    const message = error instanceof Error ? error.message : String(error);
    throw new Error(`Error updating employee: ${message}`);
  }
};

export const updateAppointmentInEmployeeProfile = async (
  originalAppointment: Appointment,
  editedAppointment: Appointment,
  siteId: string = WEBSITE_ID
): Promise<boolean> => {
  const appointmentId = originalAppointment.id;
  const previousEmployeeId = originalAppointment.employeeID;
  const nextEmployeeId = editedAppointment.employeeID;

  if (!previousEmployeeId || !nextEmployeeId) {
    throw Error("Update Appointment requires employeeID in Appointment object.");
  }

  const originalDate = originalAppointment.appointmentDate;
  const newDate = editedAppointment.appointmentDate;

  // Nothing to update if the date and employee remain the same.
  if (previousEmployeeId === nextEmployeeId && originalDate === newDate) {
    return true;
  }

  const db_website_ref: string = createBookingWebsiteDbRef(siteId);
  const previousEmployeeRef = doc(db, db_website_ref, 'employees', previousEmployeeId);

  try {
    if (previousEmployeeId === nextEmployeeId) {
      await updateDoc(previousEmployeeRef, {
        [`appointments.${originalDate}`]: arrayRemove(appointmentId),
        [`appointments.${newDate}`]: arrayUnion(appointmentId),
      });
      return true;
    }

    const batch = writeBatch(db);
    batch.update(previousEmployeeRef, {
      [`appointments.${originalDate}`]: arrayRemove(appointmentId),
    });

    const nextEmployeeRef = doc(db, db_website_ref, 'employees', nextEmployeeId);
    batch.update(nextEmployeeRef, {
      [`appointments.${newDate}`]: arrayUnion(appointmentId),
    });

    await batch.commit();
    return true;
  } catch (error) {
    console.error(`Failed to update appointment ${appointmentId} : `, error);
    throw new Error(`Failed to update the appointment ${appointmentId}`);
  }
};

// Add a new service to Firestore with optional image
export const addService = async (service: Omit<Service, 'id'>,
  serviceImage?: File,
  siteId: string = WEBSITE_ID
): Promise<string> => {
  const db_website_ref: string = createBookingWebsiteDbRef(siteId);

  let imagePath: string | undefined;
  if (serviceImage) {
    imagePath = `${db_website_ref}/services/${service.title.replace(/\s+/g, '_')}`;
    await uploadImageToStorage(serviceImage!, imagePath, /*compressionLevel=*/2);
  }
  if (imagePath) {
    service = { ...service, imagePath: imagePath };
  }

  const serviceData = { ...service };
  const docRef = await addDoc(collection(db, db_website_ref, "services"), serviceData);
  logEvent(analytics, 'added_service', { "newService": JSON.stringify(service) });
  return docRef.id;
};


export const updateService = async (
  oldService: Service,
  updatedService: Service,
  serviceImage?: File,
  siteId: string = WEBSITE_ID
): Promise<void> => {
  const db_website_ref: string = createBookingWebsiteDbRef(siteId);

  console.log("Updating service:", updatedService);
  console.log("Old service:", oldService);

  // Step 1: Handle image upload and deletion
  if (serviceImage) {
    // If the old image path is different from the new one, delete the old image
    if (oldService.imagePath && oldService.imagePath !== updatedService.imagePath) {
      await deleteImageFromStorage(oldService.imagePath);
    }

    // Upload the new image to the new path specified in updatedService
    await uploadImageToStorage(serviceImage, updatedService.imagePath!, /*compressionLevel=*/2);
  }
  // Step 2: If no image is provided in updated service 
  // but the old service has an image, delete it from storage
  else if (oldService.imagePath && !updatedService.imagePath) {
    await deleteImageFromStorage(oldService.imagePath);
  }

  // Step 3: Prepare the data to be updated in Firestore
  const serviceData: Record<string, any> = { ...updatedService };

  // If no new image is provided and there was an old image, delete the imagePath field
  if (oldService.imagePath && !updatedService.imagePath) {
    serviceData.imagePath = deleteField(); // Mark the field for deletion
  }

  // Delete category field if it's empty or null/undefined
  if (!updatedService.category || (typeof updatedService.category === 'string' && updatedService.category.trim() === '')) {
    serviceData.category = deleteField();
  }



  // Step 4: Update the service document in Firestore
  await updateDoc(doc(db, db_website_ref, "services", updatedService.id), serviceData);

  // Step 5: Log the update event for analytics
  logEvent(analytics, 'updated_service', { updatedService: JSON.stringify(updatedService) });
};

// Delete a service in Firestore by ID
export const deleteService = async (service: Service, siteId: string = WEBSITE_ID): Promise<void> => {
  const db_website_ref: string = createBookingWebsiteDbRef(siteId);
  const serviceDoc = doc(db, db_website_ref, "services", service.id);
  // Here we only have the service id, but would need the title to delete
  if (service.imagePath) {
    await deleteImageFromStorage(service.imagePath);
  }
  await deleteDoc(serviceDoc);
  logEvent(analytics, 'deleted_service', { "service": JSON.stringify(service) });
};

// Fetch all services from Firestore
export const fetchServices = async (siteId: string = WEBSITE_ID): Promise<Service[]> => {
  const db_website_ref: string = createBookingWebsiteDbRef(siteId);
  try {
    const servicesCollection = collection(db, db_website_ref, "services");
    const serviceSnapshot = await getDocs(servicesCollection);

    // Map over the documents and return an array of services
    return serviceSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as Service[];
  } catch (error) {
    console.error("Error fetching services:", error);
    throw new Error("Failed to fetch services"); // Rethrow with a user-friendly message
  }
};

export const fetchServicesBySiteId = async (siteId: string): Promise<Service[]> => {
  const db_website_ref: string = createBookingWebsiteDbRef(siteId);

  try {
    const servicesCollection = collection(db, db_website_ref, "services");
    const serviceSnapshot = await getDocs(servicesCollection);

    // Map over the documents and return an array of services
    return serviceSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as Service[];
  } catch (error) {
    console.error("Error fetching services:", error);
    throw new Error("Failed to fetch services"); // Rethrow with a user-friendly message
  }
};

// Fetch all employees from Firestore
export const fetchEmployees = async (siteId: string = WEBSITE_ID): Promise<Employee[]> => {
  const db_website_ref: string = createBookingWebsiteDbRef(siteId);
  const employeesCollection = collection(db, db_website_ref, 'employees');
  const employeeSnapshot = await getDocs(employeesCollection);
  return employeeSnapshot.docs.map((docSnap) => {
    const data = docSnap.data();
    return normalizeEmployeeRecord({
      id: docSnap.id,
      firstName: data.firstName ?? '',
      lastName: data.lastName ?? '',
      admin: data.admin ?? false,
      email: data.email ?? '',
      phone: data.phone ?? '',
      appointments: data.appointments ?? {},
      profilePictureImagePath: data.profilePictureImagePath,
      bio: data.bio,
      socials: data.socials,
      recurringBreaks: data.recurringBreaks || [],
      bookable: data.bookable,
      isOrchestrated: data.isOrchestrated ?? (data.hasLogin === false),
      active: data.active,
    } as Employee);
  });
};

export const fetchEmployeeSchedule = async (employeeId: string, siteId: string = WEBSITE_ID): Promise<Map<string, string>> => {
  const db_website_ref: string = createBookingWebsiteDbRef(siteId);

  try {
    const scheduleDocRef = doc(db, db_website_ref, 'schedules', employeeId);
    const scheduleSnapshot = await getDoc(scheduleDocRef);

    if (scheduleSnapshot.exists()) {
      // Type assertion: expect scheduleData to be an object with string keys and string values
      const scheduleData = scheduleSnapshot.data() as { [key: string]: string };

      // Convert the plain object to a Map with string-string key-value pairs
      const scheduleMap: Map<string, string> = new Map(Object.entries(scheduleData));

      return scheduleMap;  // returns a Map of string-string pairs
    } else {
      console.log("No schedule found for this employee.");
      return new Map<string, string>();
    }
  } catch (error) {
    console.error("Error fetching schedule: ", error);
    throw error;
  }
};

// Fetch a specific day's schedule bitstring for an employee
export const fetchEmployeeScheduleDay = async (
  employeeId: string,
  date: string,
  siteId: string = WEBSITE_ID
): Promise<string | null> => {
  const db_website_ref: string = createBookingWebsiteDbRef(siteId);

  try {
    const scheduleDocRef = doc(db, db_website_ref, 'schedules', employeeId);
    const scheduleSnapshot = await getDoc(scheduleDocRef);

    if (!scheduleSnapshot.exists()) {
      console.log("No schedule found for this employee.");
      return null;
    }

    const scheduleData = scheduleSnapshot.data() as { [key: string]: string };
    return scheduleData?.[date] ?? null;
  } catch (error) {
    console.error("Error fetching schedule: ", error);
    throw error;
  }
};

export const updateEmployeeSchedule = async (
  employeeId: string,
  workHours: WorkHours,
  startDate: Dayjs,
  endDate: Dayjs,
  siteId: string = WEBSITE_ID
): Promise<void> => {
  const db_website_ref: string = createBookingWebsiteDbRef(siteId);
  try {
    const dates = getDatesInRange(startDate, endDate);
    // We want to have the latest employee data for this
    const scheduleRef = doc(db, db_website_ref, 'schedules', employeeId);
    const employeeRef = doc(db, db_website_ref, 'employees', employeeId);
    const employeeSnapshot = await getDoc(employeeRef);

    if (!employeeSnapshot.exists()) {
      throw new Error(`Employee ${employeeId} not found while updating schedule`);
    }

    const employeeData = employeeSnapshot.data();
    const appointmentMap: Record<string, string[]> = employeeData?.appointments || {};

    const relevantAppointmentIdsByDate: Record<string, string[]> = {};
    const appointmentIdsToFetch = new Set<string>();

    dates.forEach((date) => {
      const formattedDate = formatDateToMMDDYYYY(date);
      const idsForDate = Array.isArray(appointmentMap?.[formattedDate]) ? appointmentMap[formattedDate] : [];
      const sanitizedIds = idsForDate.filter((id): id is string => typeof id === 'string' && id.trim().length > 0);

      if (sanitizedIds.length > 0) {
        relevantAppointmentIdsByDate[formattedDate] = sanitizedIds;
        sanitizedIds.forEach((id) => appointmentIdsToFetch.add(id));
      }
    });

    const appointmentsById = await loadAppointmentMapByIdsForSchedule(db_website_ref, appointmentIdsToFetch);

    const updates: { [key: string]: string } = {};

    for (const date of dates) {
      const formattedDate: string = formatDateToMMDDYYYY(date);
      const dayOfWeek = date.day();
      let bitString = workHours[DAYJS_DAYS_OF_WEEK[dayOfWeek]];

      const appointmentIds = relevantAppointmentIdsByDate[formattedDate];
      if (appointmentIds && appointmentIds.length > 0) {
        appointmentIds.forEach((appointmentId) => {
          const appointment = appointmentsById.get(appointmentId);
          if (!appointment) {
            return;
          }

          const startTime = appointment.appointmentTime;
          const endTime = getAppointmentEndTime(startTime, appointment.appointmentDuration);
          bitString = fillRangeInBitString(bitString, startTime, endTime, true);
        });
      }

      updates[formattedDate] = bitString;
    }

    await setDoc(scheduleRef, updates, { merge: true });
    logEvent(analytics, 'updated_employee_schedule', { "updates": JSON.stringify(updates) });


    console.log('Schedule successfully updated for all dates in the range.');
  } catch (error) {
    console.error('Error updating schedule: ', error);
    throw error;
  }
};

// Mirrors the employee's saved work hours template onto specific dates while respecting
// any existing appointments. Returns a map of date -> finalized bitstring so callers can
// update caches without re-reading the schedule document. 
// 
// Use this helper when you need to regenerate availability for multiple dates 
// (or a single date that should honor existing appointments); 
// it batches everything into one write. For direct, manual bitstring
// edits where you already have the final `BitStringData` for a single date, prefer
// `updateEmployeeSchedule_WithBitStringData`.
export const updateEmployeeSchedulesForDates = async (
  employeeId: string,
  workHours: WorkHours,
  dates: Dayjs[],
  siteId: string = WEBSITE_ID
): Promise<Record<string, string>> => {
  const db_website_ref: string = createBookingWebsiteDbRef(siteId);
  try {
    // Nothing to do if we weren't given any target dates.
    if (!Array.isArray(dates) || dates.length === 0) {
      return {};
    }

    // Normalize each entry to the start of day and drop any invalid instances.
    const normalizedDates = dates
      .map((date) => date?.startOf('day'))
      .filter((date): date is Dayjs => !!date && date.isValid());

    if (normalizedDates.length === 0) {
      return {};
    }

    // Collapse duplicates so we only process each date key once.
    const dedupedDateEntries = Array.from(
      normalizedDates.reduce((acc, date) => {
        acc.set(formatDateToMMDDYYYY(date), date);
        return acc;
      }, new Map<string, Dayjs>())
    );

    if (dedupedDateEntries.length === 0) {
      return {};
    }

    // Fetch the employee + schedule documents so we can mirror work hours and look up appointments.
    const scheduleRef = doc(db, db_website_ref, 'schedules', employeeId);

    const employee = await fetchEmployeeById(employeeId, siteId);
    if (!employee) {
      throw new Error(`Employee ${employeeId} not found while updating schedule`);
    }
    const appointmentMap: Record<string, string[]> = employee.appointments || {};

    const scheduleBitStrings = await fetchEmployeeSchedule(employeeId, siteId);

    const relevantAppointmentIdsByDate: Record<string, string[]> = {};
    const appointmentIdsToFetch = new Set<string>();

    // Build a lookup of appointments that fall on the requested dates.
    dedupedDateEntries.forEach(([dateKey]) => {
      const idsForDate = Array.isArray(appointmentMap?.[dateKey]) ? appointmentMap[dateKey] : [];
      const sanitizedIds = idsForDate.filter((id): id is string => typeof id === 'string' && id.trim().length > 0);
      if (sanitizedIds.length > 0) {
        relevantAppointmentIdsByDate[dateKey] = sanitizedIds;
        sanitizedIds.forEach((id) => appointmentIdsToFetch.add(id));
      }
    });

    // Load the appointment documents so we can carve out their occupied time ranges from the template.
    const appointmentsById = await loadAppointmentMapByIdsForSchedule(db_website_ref, appointmentIdsToFetch);
    const updates: Record<string, string> = {};

    // For each date:
    // 1. Start with the work-hours bitstring matching the weekday.
    // 2. Fill (set to busy) any segments that already have appointments.
    // 3. Persist the finished bitstring to the schedule doc.
    dedupedDateEntries.forEach(([dateKey, date]) => {
      const dayOfWeek = date.day();
      let bitString = workHours[DAYJS_DAYS_OF_WEEK[dayOfWeek]];

      const appointmentIds = relevantAppointmentIdsByDate[dateKey];
      if (appointmentIds && appointmentIds.length > 0) {
        appointmentIds.forEach((appointmentId) => {
          const appointment = appointmentsById.get(appointmentId);
          if (!appointment) {
            return;
          }
          const endTime = getAppointmentEndTime(appointment.appointmentTime, appointment.appointmentDuration);
          bitString = fillRangeInBitString(bitString, appointment.appointmentTime, endTime, true);
        });
      }

      updates[dateKey] = bitString;
      scheduleBitStrings.set(dateKey, bitString);
    });

    if (Object.keys(updates).length > 0) {
      // Merge the generated bitstrings into the schedule document to avoid overwriting other dates.
      await setDoc(scheduleRef, updates, { merge: true });
      logEvent(analytics, 'updated_employee_schedule_for_dates', { "updates": JSON.stringify(updates) });
      console.log(`Schedule successfully updated for dates: ${Object.keys(updates).join(', ')}`);
    }

    return updates;
  } catch (error) {
    console.error('Error updating schedule for dates: ', error);
    throw error;
  }
};

export const updateEmployeeScheduleForDate = async (
  employeeId: string,
  workHours: WorkHours,
  date: Dayjs,
  siteId: string = WEBSITE_ID
): Promise<string | undefined> => {
  // Convenience wrapper when the caller only needs to fill a single date from the work-hours template.
  // Defers to the batched helper so we still merge in existing appointments/blockers and keep behavior consistent.
  const updates = await updateEmployeeSchedulesForDates(employeeId, workHours, [date], siteId);
  return updates[formatDateToMMDDYYYY(date)];
};

// This loads a map of Appointment by their IDs, warning if any are missing
// "Raw" because it does not filter out blockers or completed appointments
const loadAppointmentMapByIdsRaw = async (
  dbWebsiteRef: string,
  appointmentIds: Set<string>
): Promise<Map<string, Appointment>> => {
  console.log(`Loading ${appointmentIds.size} appointment(s) from Firestore`);
  const ids = Array.from(appointmentIds).filter((id) => id && id.trim());
  if (!ids.length) return new Map();

  const colRef = collection(db, dbWebsiteRef, "appointments");
  const batches = chunk(ids, 10);

  const snaps = await Promise.all(
    batches.map((batchIds) =>
      getDocs(query(colRef, where(documentId(), "in", batchIds)))
    )
  );

  const map = new Map<string, Appointment>();
  for (const snap of snaps) {
    snap.forEach((docSnap) => {
      const data = docSnap.data() as Omit<Appointment, "id">;
      map.set(docSnap.id, { id: docSnap.id, ...data } as Appointment);
    });
  }

  // warn missing ids (optional)
  for (const id of ids) {
    if (!map.has(id)) {
      console.warn(`Appointment ${id} referenced but missing`);
    }
  }

  return map;
};

// This loads a map of Appointment by their IDs, filtering to only those that occupy the schedule
const loadAppointmentMapByIdsForSchedule = async (
  dbWebsiteRef: string,
  appointmentIds: Set<string>
): Promise<Map<string, Appointment>> => {
  const raw = await loadAppointmentMapByIdsRaw(dbWebsiteRef, appointmentIds);
  const filtered = new Map<string, Appointment>();
  raw.forEach((appt, id) => {
    if (occupiesSchedule(appt)) filtered.set(id, appt);
  });
  return filtered;
};

// Public helper that accepts an array of IDs and returns Appointment[]
//  Ideal for UI code needing the raw records.
export const getAppointmentsByIds = async (
  appointmentIds: string[],
  siteId: string = WEBSITE_ID,
  debugLabel?: string,
): Promise<Appointment[]> => {
  const dbWebsiteRef = createBookingWebsiteDbRef(siteId);
  const uniqueIds = new Set(appointmentIds.filter((id) => typeof id === "string" && id.trim()));
  if (uniqueIds.size === 0) {
    return [];
  }
  const labelSuffix = debugLabel ? ` [${debugLabel}]` : "";
  console.log(`Loading ${uniqueIds.size} appointment(s) from Firestore${labelSuffix}`);
  //  small number of IDs, use getAppointmentById to avoid a batch read
  if (uniqueIds.size <= 10) {
    console.log(`[getAppointmentsByIds] Using per-id fetch for ${uniqueIds.size} appointment(s)${labelSuffix}`);
    const idsArray = Array.from(uniqueIds);
    const fetched = await Promise.all(
      idsArray.map(async (id) => {
        try {
          return await getAppointmentById(id, siteId);
        } catch (error) {
          if (isNotFoundError(error)) {
            console.warn(`Appointment ${id} referenced but missing`);
            return null;
          }
          throw error;
        }
      })
    );
    return fetched.filter((appt): appt is Appointment => appt !== null);
  }
  console.log(`[getAppointmentsByIds] Using batched fetch for ${uniqueIds.size} appointment(s)${labelSuffix}`);
  const map = await loadAppointmentMapByIdsRaw(dbWebsiteRef, uniqueIds);
  return Array.from(map.values());
};


const findRecurringBreakConflicts = (
  appointmentLookup: Map<string, Appointment>,
  appointmentMap: Employee['appointments'],
  instance: RecurringBreakInstance
): Appointment[] => {
  const dateKey = instance.start.format('MMDDYYYY');
  const appointmentIds = appointmentMap?.[dateKey] || [];
  if (!appointmentIds || appointmentIds.length === 0) {
    return [];
  }

  return appointmentIds
    .map((id) => appointmentLookup.get(id))
    .filter((appointment): appointment is Appointment => !!appointment && (isBlocker(appointment) || isUpcomingStatus(appointment.status)))
    .filter((appointment) => {
      const day = parseDateFromMMDDYYYY(appointment.appointmentDate)
        .hour(parseInt(appointment.appointmentTime.substring(0, 2), 10))
        .minute(parseInt(appointment.appointmentTime.substring(2, 4), 10))
        .second(0);
      const appointmentEnd = day.add(appointment.appointmentDuration, 'minute');
      return day.isBefore(instance.end) && appointmentEnd.isAfter(instance.start);
    });
};

const cancelAppointmentsAndDeleteBlockers = async (
  appointments: Appointment[],
  scheduleBitStrings?: Map<string, string>,
  siteId: string = WEBSITE_ID,
): Promise<void> => {
  if (!appointments || appointments.length === 0) {
    return;
  }
  const blockers = appointments.filter(isBlocker);
  const realAppointments = appointments.filter((appointment) => !isBlocker(appointment));

  if (realAppointments.length > 0) {
    await cancelAppointments(realAppointments, scheduleBitStrings, siteId);
  }
  if (blockers.length > 0) {
    await Promise.all(blockers.map((blocker) => deleteBlocker(blocker, siteId, scheduleBitStrings)));
  }
};

export const addToEmployeeAppointments = async (employeeId: string, appointmentId: string, date: string, siteId: string = WEBSITE_ID): Promise<boolean> => {
  const db_website_ref: string = createBookingWebsiteDbRef(siteId);
  try {
    const employeeDocRef = doc(db, db_website_ref, "employees", employeeId);
    // Use arrayUnion to add the appointment ID to the array for the specific date
    await updateDoc(employeeDocRef, {
      [`appointments.${date}`]: arrayUnion(appointmentId),
    });

    console.log(`Successfully added appointment ${appointmentId} to employee ${employeeId}'s appointments on date ${date}.`);
    return true;

  } catch (error) {
    console.error("Error adding appointment:", error);
    return false;
  }
};

// Add a new appointment to Firestore
export const createAppointment = async (appointmentData: Omit<Appointment, 'id'>, siteId: string = WEBSITE_ID): Promise<string> => {
  const db_website_ref: string = createBookingWebsiteDbRef(siteId);
  const shortId = nanoid();
  const docRef = doc(db, db_website_ref, "appointments", shortId);

  try {
    // Skip the defensive existence read and rely on random nanoid collisions
    // being astronomically rare. This removes one Firestore read per
    // appointment/blocker creation.
    await setDoc(docRef, { ...appointmentData, id: shortId });
    logEvent(analytics, 'created_appointment', { "appointment": JSON.stringify(appointmentData), "id": shortId });
    return shortId;
  } catch (error) {
    throw new Error("Failed to book appointment");
  }
};

export type CreateBlockerInput = {
  employee: Employee;
  start: Dayjs;
  end: Dayjs;
  title: string;
  siteId?: string;
  recurringBreakId?: string;
  /**
   * Optional cache of schedule bitstrings keyed by date. When provided (e.g. the
   * calendar screen already has the Map) we can update Firestore without
   * performing another read, which saves a round trip per blocker.
   */
  scheduleBitStrings?: Map<string, string>;
};

export const createBlocker = async ({
  employee,
  start,
  end,
  title,
  siteId = WEBSITE_ID,
  recurringBreakId,
  scheduleBitStrings,
}: CreateBlockerInput): Promise<Appointment> => {
  if (!employee?.id) {
    throw new Error('Employee is required to create a blocker');
  }
  if (!start || !end) {
    throw new Error('Invalid blocker time range');
  }

  const duration = end.diff(start, 'minute');
  if (duration <= 0) {
    throw new Error('Blocker must end after it starts');
  }

  const appointmentDate = start.format('MMDDYYYY');
  const appointmentTime = start.format('HHmm');
  const sanitizedTitle = title?.trim() || 'Unavailable';

  const db_website_ref: string = createBookingWebsiteDbRef(siteId);
  let scheduleUpdated = false;
  const cachedBitString = scheduleBitStrings?.get(appointmentDate);
  const hadBitString = Boolean(cachedBitString);
  if (cachedBitString) {
    // Reuse the already-fetched schedule bitstring instead of re-reading the
    // schedule document. This saves one Firestore read per blocker creation.
    const updated = updateScheduleBitString(cachedBitString, appointmentTime, duration, false);
    if (!updated) {
      throw new Error('Slot unavailable—remove the existing item first.');
    }
    const scheduleRef = doc(db, db_website_ref, 'schedules', employee.id);
    await updateDoc(scheduleRef, { [appointmentDate]: updated });
    scheduleBitStrings?.set(appointmentDate, updated);
    scheduleUpdated = true;
  } else {
    scheduleUpdated = await addBookingToSchedule(
      employee.id,
      appointmentDate,
      appointmentTime,
      duration,
      false,
      scheduleBitStrings,
      siteId,
    );
  }

  if (!scheduleUpdated) {
    const stillMissingBitString = !hadBitString && !scheduleBitStrings?.get(appointmentDate);
    if (stillMissingBitString) {
      throw new Error('No schedule exists for this date. Please add your work hours first.');
    }
    throw new Error('Slot unavailable—remove the existing item first.');
  }

  const businessUrl = typeof window !== 'undefined'
    ? `${window.location.protocol}//${window.location.host}`
    : '';

  const blockerBase: Omit<Appointment, 'id'> = {
    employeeID: employee.id,
    employeeName: `${employee.firstName} ${employee.lastName}`.trim(),
    employeeEmail: employee.email || '',
    employeePhoneNumber: employee.phone || '',
    customerFirstName: employee.firstName || '',
    customerLastName: employee.lastName || '',
    customerPhoneNumber: employee.phone || '',
    customerEmail: employee.email || '',
    billingAmount: 0,
    creationTime: Timestamp.now(),
    appointmentDate,
    appointmentTime,
    status: AppointmentStatus.NON_APPOINTMENT,
    appointmentTitle: 'Unavailable',
    appointmentDuration: duration,
    servicesList: [],
    businessName: 'Internal Blocker',
    businessUrl,
    lastUpdater: employee.id,
    allowSendCustomerSMS: false,
    allowSendEmployeeSMS: false,
    notes: sanitizedTitle,
  };

  if (recurringBreakId) {
    blockerBase.recurringBreakId = recurringBreakId;
  }

  let appointmentId: string | null = null;
  try {
    appointmentId = await createAppointment(blockerBase, siteId);
    const linked = await addToEmployeeAppointments(employee.id, appointmentId, appointmentDate, siteId);
    if (!linked) {
      throw new Error('Failed to attach blocker to employee profile');
    }
    return { ...blockerBase, id: appointmentId };
  } catch (error) {
    console.error('Failed to create blocker', error);
    if (appointmentId) {
      await deleteAppointment({ ...blockerBase, id: appointmentId }, siteId);
    } else {
      const rollbackBitString = scheduleBitStrings?.get(appointmentDate);
      if (rollbackBitString) {
        const updated = updateScheduleBitString(rollbackBitString, appointmentTime, duration, true);
        if (updated) {
          const scheduleRef = doc(db, db_website_ref, 'schedules', employee.id);
          await updateDoc(scheduleRef, { [appointmentDate]: updated });
          scheduleBitStrings?.set(appointmentDate, updated);
        }
      } else {
        await addBookingToSchedule(
          employee.id,
          appointmentDate,
          appointmentTime,
          duration,
          true,
          scheduleBitStrings,
          siteId,
        );
      }
    }
    throw error;
  }
};

export const deleteBlocker = async (
  appointment: Appointment,
  siteId: string = WEBSITE_ID,
  scheduleBitStrings?: Map<string, string>,
): Promise<void> => {
  if (!isBlocker(appointment)) {
    throw new Error('deleteBlocker can only be used for blocker appointments');
  }
  await deleteAppointment(appointment, siteId, scheduleBitStrings);
};

export type GenerateRecurringBreakBlockersInput = {
  employeeId: string;
  windowStart: Dayjs;
  windowEnd: Dayjs;
  siteId?: string;
  recurringBreakIdsToReset?: string[];
  scheduleBitStrings?: Map<string, string>;
};

export type GenerateRecurringBreakBlockersResult = {
  updatedAppointmentsByDate: Record<string, string[]>;
  createdBlockers: Appointment[];
  deletedBlockerIds: string[];
  cancelledAppointmentIds: string[];
};

export const RECURRING_BREAK_PARTIAL_FAILURE_CODE = 'RECURRING_BREAK_PARTIAL_FAILURE' as const;

export type RecurringBreakPartialFailureDetails = {
  employeeId: string;
  failedDeletes: string[];
  failedCreates: string[];
  failedCancels: string[];
};

export type RecurringBreakPartialFailureError = Error & {
  code: typeof RECURRING_BREAK_PARTIAL_FAILURE_CODE;
  details: RecurringBreakPartialFailureDetails;
};

const createRecurringBreakPartialFailureError = (
  details: RecurringBreakPartialFailureDetails
): RecurringBreakPartialFailureError => {
  const message = `Recurring break regeneration partially failed (delete: ${details.failedDeletes.length}, create: ${details.failedCreates.length}, cancel: ${details.failedCancels.length}).`;
  const error = new Error(message) as RecurringBreakPartialFailureError;
  error.code = RECURRING_BREAK_PARTIAL_FAILURE_CODE;
  error.details = details;
  return error;
};

export const isRecurringBreakPartialFailureError = (
  error: unknown
): error is RecurringBreakPartialFailureError =>
  Boolean(
    error &&
    typeof error === 'object' &&
    'code' in error &&
    (error as { code?: string }).code === RECURRING_BREAK_PARTIAL_FAILURE_CODE &&
    'details' in error
  );

// Generate recurring break blockers for an employee within a specified date range.
export const generateRecurringBreakBlockersForEmployee = async ({
  employeeId,
  windowStart,
  windowEnd,
  siteId = WEBSITE_ID,
  recurringBreakIdsToReset,
  scheduleBitStrings,
}: GenerateRecurringBreakBlockersInput): Promise<GenerateRecurringBreakBlockersResult> => {
  if (!employeeId) {
    throw new Error('Employee ID is required to generate recurring blockers');
  }
  const employee = await fetchEmployeeById(employeeId, siteId);
  if (!employee) {
    throw new Error('Unable to load employee for recurring break generation');
  }

  const updatedAppointmentsByDate: Record<string, string[]> = Object.entries(employee.appointments || {}).reduce(
    (acc, [dateKey, ids]) => {
      acc[dateKey] = Array.isArray(ids) ? [...ids] : [];
      return acc;
    },
    {} as Record<string, string[]>
  );
  const createdBlockers: Appointment[] = [];
  const deletedBlockerIds = new Set<string>();
  const cancelledAppointmentIds = new Set<string>();
  const failedDeleteIds = new Set<string>();
  const failedCreateKeys = new Set<string>();
  const failedCancelIds = new Set<string>();
  const removeAppointmentFromIndex = (dateKey: string, appointmentId: string) => {
    const existing = updatedAppointmentsByDate[dateKey] ?? [];
    const filtered = existing.filter((id) => id !== appointmentId);
    if (filtered.length === 0) {
      delete updatedAppointmentsByDate[dateKey];
      return;
    }
    updatedAppointmentsByDate[dateKey] = filtered;
  };
  const addAppointmentToIndex = (dateKey: string, appointmentId: string) => {
    const existing = updatedAppointmentsByDate[dateKey] ?? [];
    if (existing.includes(appointmentId)) {
      return;
    }
    updatedAppointmentsByDate[dateKey] = [...existing, appointmentId];
  };
  const buildResult = (): GenerateRecurringBreakBlockersResult => ({
    updatedAppointmentsByDate,
    createdBlockers,
    deletedBlockerIds: Array.from(deletedBlockerIds),
    cancelledAppointmentIds: Array.from(cancelledAppointmentIds),
  });
  const throwIfPartialFailures = () => {
    if (failedDeleteIds.size > 0 || failedCreateKeys.size > 0 || failedCancelIds.size > 0) {
      throw createRecurringBreakPartialFailureError({
        employeeId,
        failedDeletes: Array.from(failedDeleteIds),
        failedCreates: Array.from(failedCreateKeys),
        failedCancels: Array.from(failedCancelIds),
      });
    }
  };

  const definitions = employee.recurringBreaks || [];
  const hasExplicitResetList = Array.isArray(recurringBreakIdsToReset);
  // Step 1: decide which recurring-break IDs must be regenerated. Prefer the caller's
  // explicit list; if they didn't send one, start with every definition they currently have.
  const idsToResetSource = hasExplicitResetList
    ? (recurringBreakIdsToReset ?? [])
    : definitions.map((def) => def.id);
  const idsToReset = new Set(idsToResetSource.filter(Boolean));
  if (hasExplicitResetList && idsToReset.size === 0) {
    // If the caller explicitly requested no definitions to reset, skip the rest.
    return buildResult();
  }

  // Step 2: normalize the date range we need to inspect.
  const windowStartBound = windowStart.startOf('day');
  const windowEndBound = windowEnd.endOf('day');

  // Step 3: fetch every appointment in the range once so we can do all filtering locally.
  const appointmentIdsToCheck = collectAppointmentIdsWithinWindow(
    updatedAppointmentsByDate,
    windowStartBound,
    windowEndBound
  );
  // If we have no definitions to reset and no appointments in the range, skip.
  if (!hasExplicitResetList && idsToReset.size === 0 && appointmentIdsToCheck.size === 0) {
    return buildResult();
  }

  const appointmentIds = Array.from(appointmentIdsToCheck);
  const appointmentsInWindow = appointmentIds.length > 0
    ? await getAppointmentsByIds(appointmentIds, siteId, 'generateRecurringBreakBlockers')
    : [];
  const appointmentLookup = new Map(appointmentsInWindow.map((appt) => [appt.id, appt]));

  // When we don't have an explicit reset list, we need to look at all blockers to see if any are already in the window. This helps us avoid creating duplicates.
  if (!hasExplicitResetList) {
    // Step 4: when we own the diffing, add any blocker IDs we found so deleted definitions
    // automatically clean themselves up.
    appointmentsInWindow.forEach((appointment) => {
      if (!isBlocker(appointment) || !appointment.recurringBreakId) {
        return;
      }
      idsToReset.add(appointment.recurringBreakId);
    });
  }

  // If we have no definitions to reset, skip the rest.
  if (idsToReset.size === 0) {
    return buildResult();
  }

  // Step 5: group any existing blockers by recurring-break ID so we can reuse/delete in bulk.
  const blockersByDefinition = new Map<string, Appointment[]>();
  appointmentsInWindow.forEach((appointment) => {
    if (!isBlocker(appointment) || !appointment.recurringBreakId) {
      return;
    }
    if (!idsToReset.has(appointment.recurringBreakId)) {
      return;
    }
    const blockers = blockersByDefinition.get(appointment.recurringBreakId) ?? [];
    blockers.push(appointment);
    blockersByDefinition.set(appointment.recurringBreakId, blockers);
  });

  // Helper: remove a batch of blockers (used for stale instances as we progress).
  const removeBlockers = async (blockers: Appointment[]) => {
    if (blockers.length === 0) {
      console.log('[RecurringBreaks] removeBlockers invoked with empty list', {
        employeeId,
        windowStart: windowStartBound.format('YYYY-MM-DD'),
        windowEnd: windowEndBound.format('YYYY-MM-DD'),
      });
      return;
    }

    await Promise.all(
      blockers.map(async (blocker) => {
        try {
          await deleteBlocker(blocker, siteId, scheduleBitStrings);
          appointmentLookup.delete(blocker.id);
          removeAppointmentFromIndex(blocker.appointmentDate, blocker.id);
          deletedBlockerIds.add(blocker.id);
        } catch (error) {
          console.error('Failed to delete recurring break blocker', blocker.id, error);
          failedDeleteIds.add(blocker.id);
        }
      })
    );
  };

  // Step 6: purge blockers for definitions that no longer exist.
  const existingDefinitionIds = new Set(definitions.map((definition) => definition.id).filter(Boolean));
  const removedDefinitionIds = Array.from(idsToReset).filter((id) => !!id && !existingDefinitionIds.has(id));
  if (removedDefinitionIds.length > 0) {
    console.log('[RecurringBreaks] Removing blockers for definitions that were deleted before regeneration', {
      employeeId,
      removedDefinitionIds,
      rangeStart: windowStartBound.format('YYYY-MM-DD'),
      rangeEnd: windowEndBound.format('YYYY-MM-DD'),
    });
    const blockersToDelete = removedDefinitionIds.flatMap((id) => blockersByDefinition.get(id) ?? []);
    await removeBlockers(blockersToDelete);
    removedDefinitionIds.forEach((id) => blockersByDefinition.delete(id));
  }

  // Step 7: determine which definitions actually need blockers generated inside this window.
  const definitionsToGenerate = definitions.filter((def) => idsToReset.has(def.id));
  if (definitionsToGenerate.length === 0) {
    console.log('[RecurringBreaks] Reset list provided but none of the IDs map to employee definitions', {
      employeeId,
      idsRequested: Array.from(idsToReset),
    });
    const staleBlockers = Array.from(blockersByDefinition.values()).flat();
    await removeBlockers(staleBlockers);
    throwIfPartialFailures();
    return buildResult();
  }

  // Step 8: expand each definition into concrete instances and reconcile them one by one.
  const instances = getRecurringBreakInstances(definitionsToGenerate, windowStartBound, windowEndBound);
  // Sort instances by date and time to ensure consistent processing order
  for (const instance of instances) {
    const scopeBlockers = blockersByDefinition.get(instance.definitionId) ?? [];
    const instanceDuration = instance.end.diff(instance.start, 'minute');
    const instanceDateKey = instance.start.format('MMDDYYYY');
    const instanceTimeKey = instance.start.format('HHmm');
    const existingIndex = scopeBlockers.findIndex((blocker) => {
      return (
        blocker.appointmentDate === instanceDateKey &&
        blocker.appointmentTime === instanceTimeKey &&
        blocker.appointmentDuration === instanceDuration
      );
    });

    // If the exact instance already exists, we can skip creating it again.
    if (existingIndex >= 0) {
      const [existingBlocker] = scopeBlockers.splice(existingIndex, 1);
      appointmentLookup.delete(existingBlocker.id);
      continue;
    }

    // Before creating the blocker, cancel any conflicts occupying that slot.
    const conflictsForInstance = findRecurringBreakConflicts(
      appointmentLookup,
      updatedAppointmentsByDate,
      instance
    );
    if (conflictsForInstance.length > 0) {
      try {
        await cancelAppointmentsAndDeleteBlockers(conflictsForInstance, scheduleBitStrings, siteId);
        conflictsForInstance.forEach((appointment) => {
          appointmentLookup.delete(appointment.id);
          removeAppointmentFromIndex(appointment.appointmentDate, appointment.id);
          if (isBlocker(appointment)) {
            deletedBlockerIds.add(appointment.id);
          } else {
            cancelledAppointmentIds.add(appointment.id);
          }
        });
      } catch (error) {
        console.error(
          `Failed to cancel appointments blocking recurring break ${instance.definitionId}`,
          error
        );
        conflictsForInstance.forEach((appointment) => {
          if (isBlocker(appointment)) {
            failedDeleteIds.add(appointment.id);
          } else {
            failedCancelIds.add(appointment.id);
          }
        });
        continue;
      }
    }

    try {
      const createdBlocker = await createBlocker({
        employee,
        start: instance.start,
        end: instance.end,
        title: 'Break',
        siteId,
        recurringBreakId: instance.definitionId,
        scheduleBitStrings,
      });
      createdBlockers.push(createdBlocker);
      appointmentLookup.set(createdBlocker.id, createdBlocker);
      addAppointmentToIndex(createdBlocker.appointmentDate, createdBlocker.id);
    } catch (error) {
      const isSlotUnavailableError = error instanceof Error && error.message.includes('Slot unavailable');
      if (isSlotUnavailableError) {
        // We already marked this exact time range as busy (likely from a previous
        // run), so trying to create it again will fail with "Slot unavailable".
        // That's fine—nothing further is required—so log a friendly message and
        // skip the error spam to keep recurring break saves idempotent.
        console.info(
          `Recurring break ${instance.definitionId} already has a blocker for ${instance.start.format('MMDDYYYY')} ${instance.start.format('HHmm')}. Skipping regeneration.`
        );
      } else {
        console.error(`Failed to create recurring break blocker for ${instance.definitionId}`, error);
        failedCreateKeys.add(`${instance.definitionId}:${instanceDateKey}:${instanceTimeKey}`);
      }
    }
  }

  // Step 9: any blockers left in the map were unmatched, so delete them now.
  const blockersToRemove: Appointment[] = [];
  blockersByDefinition.forEach((blockers) => {
    blockersToRemove.push(...blockers);
  });

  await removeBlockers(blockersToRemove);
  throwIfPartialFailures();
  return buildResult();
};

export const updateEmployeeSchedule_WithBitStringData = async (
  employeeId: string,
  date: Dayjs,
  updatedSchedule: BitStringData,
  scheduleBitStrings?: Map<string, string>,
  siteId: string = WEBSITE_ID,
) => {
  const db_website_ref: string = createBookingWebsiteDbRef(siteId);

  try {
    // Construct the formatted date string
    const formattedDate = date.format("MMDDYYYY");

    const scheduleDocRef = doc(db, db_website_ref, "schedules", employeeId);
    // Update or create the entry directly without re-reading the document.
    await setDoc(scheduleDocRef, { [formattedDate]: updatedSchedule.bitString }, { merge: true });
    scheduleBitStrings?.set(formattedDate, updatedSchedule.bitString);
    logEvent(analytics, 'updated_employee_schedule_with_bitstring', { "date": formattedDate, "updatedSchedule": JSON.stringify(updatedSchedule) });

    console.log("Schedule updated successfully for employee:", employeeId);
  } catch (error) {
    console.error("Error updating schedule for employee:", employeeId, error);
    throw new Error("Error updating schedule for employee");
  }
};

// TODO this name is too vauge needs to change 
export const addBookingToSchedule = async (
  employeeId: string,
  date: string,
  selectedTime: string,
  appointmentDuration: number,
  cancel: boolean = false,
  scheduleBitStrings?: Map<string, string>,
  siteId: string = WEBSITE_ID,
  options?: {
    throwOnOperationalError?: boolean;
  },
): Promise<boolean> => {
  const db_website_ref: string = createBookingWebsiteDbRef(siteId);
  const throwOnOperationalError = options?.throwOnOperationalError === true;

  try {
    const scheduleDocRef = doc(db, db_website_ref, 'schedules', employeeId);
    const cachedBitString = scheduleBitStrings?.get(date);

    let bitString: string | undefined = cachedBitString;
    if (!bitString) {
      // Fetch the employee's schedule document only if the cache does not contain the date.
      const scheduleDoc = await getDoc(scheduleDocRef);

      if (!scheduleDoc.exists()) {
        console.log("Schedule document does not exist for the employee:", employeeId);
        if (throwOnOperationalError) {
          throw new Error(`Missing schedule document for employee ${employeeId}`);
        }
        return false;
      }

      console.log("Schedule document found for date:", date);
      const scheduleData = scheduleDoc.data();
      bitString = scheduleData[date];
      if (!bitString) {
        console.log(`No schedule found for date: ${date}`);
        if (throwOnOperationalError) {
          throw new Error(`Missing schedule data for employee ${employeeId} on ${date}`);
        }
        return false;
      }
    }

    const updatedBitString = updateScheduleBitString(bitString, selectedTime, appointmentDuration, cancel);

    if (!updatedBitString) {
      console.log("Time slot is occupied or could not be updated.");
      return false;
    }

    // Step 3: Update the employee's schedule in Firestore
    await setDoc(scheduleDocRef, { [date]: updatedBitString }, { merge: true });
    scheduleBitStrings?.set(date, updatedBitString);
    logEvent(analytics, 'add_booking_to_schedule', { "date": date, "bitstring": updatedBitString });

    console.log("Schedule updated successfully for employee:", employeeId);
    return true;
  } catch (error) {
    console.error("Error updating schedule for employee:", employeeId, error);
    if (throwOnOperationalError) {
      throw error;
    }
    return false;
  }
};

export const createCustomer = async (customerData: CustomerCreateInput, siteId: string = WEBSITE_ID): Promise<void> => {
  const { id, ...restOfCustomerData } = customerData;
  const db_website_ref: string = createBookingWebsiteDbRef(siteId);

  // Reference to the "customers" collection with the phone number as the document ID
  const customerRef = doc(collection(db, db_website_ref, 'customers'), id);
  try {
    // Set the document with the given phone number as the ID, and merge the data
    const updatePayload: Record<string, any> = { ...restOfCustomerData };
    if (restOfCustomerData.employeeIds && restOfCustomerData.employeeIds.length > 0) {
      updatePayload.employeeIds = arrayUnion(...restOfCustomerData.employeeIds);
    }
    await setDoc(customerRef, updatePayload, { merge: true });
    console.log(`Customer data saved/updated for phone: ${id}`);
  } catch (error) {
    console.error('Error creating/updating customer:', error);
  }
};

type CustomerPage = {
  customers: Customer[];
  lastDoc: QueryDocumentSnapshot<DocumentData> | null;
  hasMore: boolean;
};

export const fetchCustomersPage = async ({
  siteId = WEBSITE_ID,
  employeeId,
  pageSize = 50,
  cursor,
}: {
  siteId?: string;
  employeeId?: string | null;
  pageSize?: number;
  cursor?: QueryDocumentSnapshot<DocumentData> | null;
}): Promise<CustomerPage> => {
  const db_website_ref: string = createBookingWebsiteDbRef(siteId);
  const customersRef = collection(db, db_website_ref, 'customers');
  const constraints: QueryConstraint[] = [];

  if (employeeId) {
    constraints.push(where('employeeIds', 'array-contains', employeeId));
  }
  constraints.push(orderBy('lastName'));
  if (cursor) {
    constraints.push(startAfter(cursor));
  }
  constraints.push(limit(pageSize));

  const snapshot = await getDocs(query(customersRef, ...constraints));
  const customers: Customer[] = snapshot.docs.map((docSnap) => {
    const data = docSnap.data() as Partial<Customer>;
    return {
      id: docSnap.id,
      firstName: data.firstName ?? '',
      lastName: data.lastName ?? '',
      email: data.email ?? '',
      phone: data.phone ?? docSnap.id,
      totalAppointments: data.totalAppointments ?? 0,
      totalCompletedRevenue: data.totalCompletedRevenue ?? 0,
      lastAppointmentDate: data.lastAppointmentDate ?? null,
      employeeIds: Array.isArray(data.employeeIds) ? data.employeeIds.filter(Boolean) : [],
    };
  });

  const lastDoc = snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null;
  const hasMore = snapshot.size === pageSize;
  return { customers, lastDoc, hasMore };
};

// This will take a single appointment and remove it from the schedule of a employee and mark that appointment as cancelled in the appointment db.
export const cancelAppointment = async (
  appointment: Appointment,
  lastUpdater: string = appointment.employeeID,
  scheduleBitStrings?: Map<string, string>,
  siteId: string = WEBSITE_ID,
): Promise<void> => {
  const db_website_ref: string = createBookingWebsiteDbRef(siteId);

  const employeeDocRef = doc(db, db_website_ref, 'employees', appointment.employeeID);
  const scheduleDocRef = doc(db, db_website_ref, 'schedules', appointment.employeeID);

  const releaseSlotFromCache = async () => {
    if (!scheduleBitStrings) {
      return addBookingToSchedule(
        appointment.employeeID,
        appointment.appointmentDate,
        appointment.appointmentTime,
        appointment.appointmentDuration,
        /*cancel=*/true,
        /*scheduleBitStrings=*/undefined,
        siteId
      );
    }

    const cached = scheduleBitStrings.get(appointment.appointmentDate);
    if (!cached) {
      return addBookingToSchedule(
        appointment.employeeID,
        appointment.appointmentDate,
        appointment.appointmentTime,
        appointment.appointmentDuration,
        /*cancel=*/true,
        scheduleBitStrings,
        siteId,
      );
    }

    const updatedBitString = updateScheduleBitString(
      cached,
      appointment.appointmentTime,
      appointment.appointmentDuration,
      true
    );

    if (!updatedBitString) {
      console.warn('Failed to free schedule slot for appointment', appointment.id);
      return false;
    }

    await updateDoc(scheduleDocRef, { [appointment.appointmentDate]: updatedBitString });
    scheduleBitStrings.set(appointment.appointmentDate, updatedBitString);
    return true;
  };

  try {
    // Step 1: Change the appointment status to "CANCELLED", and mark the last updater
    await markAppointmentCancel(appointment, lastUpdater, siteId);

    // Step 2: Remove the appointment from the employee schedule
    const scheduleUpdated = await releaseSlotFromCache();

    if (!scheduleUpdated) {
      throw new Error(`Failed to update the schedule for employee ${appointment.employeeID}`);
    }

    // Step 3: Remove the appointment ID from the employee's appointments list without re-reading the document. This is because we assume the appointment exists and we just want to remove it. This is optimized because we don't need to read the employee document again.
    await updateDoc(employeeDocRef, {
      [`appointments.${appointment.appointmentDate}`]: arrayRemove(appointment.id),
    });
    logEvent(analytics, 'appointment_cancelled', { "appointment": appointment.id });
    console.log(`Appointment ${appointment.id} removed from employee ${appointment.employeeID}'s appointments list`);
  } catch (error) {
    console.error('Error cancelling appointment:', error);
    throw error;
  }
};

// This will delete the appointment from the appointment db and remove it from the employee schedule and employee appointment list.
export const deleteAppointment = async (
  appointment: Appointment,
  siteId: string = WEBSITE_ID,
  scheduleBitStrings?: Map<string, string>,
): Promise<void> => {
  const db_website_ref: string = createBookingWebsiteDbRef(siteId);

  const employeeDocRef = doc(db, db_website_ref, 'employees', appointment.employeeID);
  const appointmentDocRef = doc(db, db_website_ref, 'appointments', appointment.id);

  try {
    // Step 1: Delete the appointment object from the appointments collection
    await deleteDoc(appointmentDocRef);

    // Step 2: Remove the appointment from the employee schedule
    const scheduleUpdated = await addBookingToSchedule(
      appointment.employeeID,
      appointment.appointmentDate,
      appointment.appointmentTime,
      appointment.appointmentDuration,
      /*cancel=*/true, // free up the slot
      scheduleBitStrings,
      siteId
    );

    if (!scheduleUpdated) {
      if (isBlocker(appointment)) {
        // If this happens when removing blockers, it's not a big deal because we're just trying to clean up. We can log a warning and continue.
        console.warn(
          `Best-effort delete: unable to free schedule slot for blocker ${appointment.id}, continuing.`
        );
      } else {
        throw new Error(`Failed to update the schedule for employee ${appointment.employeeID}`);
      }
    }

    // Step 3: Remove the appointment ID from the employee's appointments list without re-reading the document
    await updateDoc(employeeDocRef, {
      [`appointments.${appointment.appointmentDate}`]: arrayRemove(appointment.id),
    });

    logEvent(analytics, 'appointment_deleted', { appointment: appointment.id });
    console.log(`Appointment ${appointment.id} deleted and removed from employee ${appointment.employeeID}'s list`);
  } catch (error) {
    console.error('Error deleting appointment:', error);
    throw error;
  }
};

export const markAppointmentCancel = async (
  appointment: Appointment,
  lastUpdater: string,
  siteId: string = WEBSITE_ID
): Promise<void> => {
  const db_website_ref: string = createBookingWebsiteDbRef(siteId);
  const appointmentDocRef = doc(db, db_website_ref, 'appointments', appointment.id);

  try {
    await updateDoc(appointmentDocRef, {
      status: 'CANCELLED',
      lastUpdater: lastUpdater,
    });

    logEvent(analytics, 'appointment_cancelled', { appointment: appointment.id });
  } catch (error) {
    console.error('❌ Error cancelling appointment:', error);
    throw error;
  }
};

// This marks the appointment as COMPLETE and updates the lastUpdater
export const markAppointmentComplete = async (
  appointment: Appointment,
  lastUpdater: string = appointment.employeeID,
  scheduleBitStrings?: Map<string, string>,
  siteId: string = WEBSITE_ID,
): Promise<void> => {
  const db_website_ref: string = createBookingWebsiteDbRef(siteId);
  const appointmentDocRef = doc(db, db_website_ref, 'appointments', appointment.id);

  try {
    console.log("Marking appointment as COMPLETE:", appointment.id);
    await updateDoc(appointmentDocRef, {
      status: 'COMPLETE',
      lastUpdater: lastUpdater,
    });
    // If the appointment start time is still in the future, free the employee's slot
    if (!isAppointmentStartTimePast(appointment)) {
      // Remove the appointment from the employee schedule
      const scheduleUpdated = await addBookingToSchedule(
        appointment.employeeID,
        appointment.appointmentDate,
        appointment.appointmentTime,
        appointment.appointmentDuration,
      /*cancel=*/true, // `cancel` is set to true to free up the slot
        scheduleBitStrings,
        siteId,
      );

      if (!scheduleUpdated) {
        throw new Error(`Failed to update the schedule for employee ${appointment.employeeID}`);
      }
    }

    logEvent(analytics, 'appointment_completed', { appointment: appointment.id });
    console.log(`✅ Appointment ${appointment.id} marked as COMPLETE`);
  } catch (error) {
    console.error('❌ Error completing appointment:', error);
    throw error;
  }
};

// Cancel multiple appointments
export async function cancelAppointments(
  appointments: Appointment[],
  scheduleBitStrings?: Map<string, string>,
  siteId: string = WEBSITE_ID,
): Promise<void> {
  try {
    const realAppointments = appointments.filter(isRealAppointment);
    await Promise.all(
      realAppointments.map(async (appointment) => {
        await cancelAppointment(appointment, /*last_updater=*/appointment.employeeID, scheduleBitStrings, siteId);
      })
    );

    console.log("All appointments have been successfully cancelled.");
  } catch (error) {
    console.error("Error cancelling multiple appointments:", error);
    throw new Error("Failed to cancel some or all appointments.");
  }
}

export const createAndStoreEmployeeSignUpInvite = async (
  isAdmin: boolean,
  services: string[],
  siteId: string = WEBSITE_ID,
  urlHost?: string
) => {
  // 1. Generate invite code
  const array = new Uint8Array(8);
  crypto.getRandomValues(array);
  const accessCode = Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('').toUpperCase();

  const expiryTime = Timestamp.fromMillis(Date.now() + 2 * 60 * 60 * 1000);

  // 2. Build base URL
  let url = window.location.origin;
  if (urlHost) {
    url = `https://${urlHost}.scheduify.com`;
  }

  // 3. Limit to 5 invites per site
  const invitesRef = collection(db, 'invites');
  const siteInvitesQuery = query(
    invitesRef,
    where('websiteId', '==', siteId),
    orderBy('expiryTime', 'asc'),
    limit(1) // only need the oldest one
  );

  const siteInvitesSnapshot = await getDocs(siteInvitesQuery);
  const totalSiteInvites = (await getDocs(query(invitesRef, where('websiteId', '==', siteId)))).size;

  if (totalSiteInvites >= 5 && !siteInvitesSnapshot.empty) {
    const oldestInviteDoc = siteInvitesSnapshot.docs[0];
    await deleteDoc(oldestInviteDoc.ref);
  }

  // 4. Store new invite
  const inviteRef = doc(db, 'invites', accessCode);
  await setDoc(inviteRef, {
    isAdmin,
    expiryTime,
    accessCode,
    services,
    websiteId: siteId,
  });

  return `${url}/signup?code=${accessCode}`;
};

export const fetchEmployeeSignUpInvite = async (code: string): Promise<EmployeeInvitation> => {
  try {
    const inviteRef = doc(db, 'invites', code);
    const inviteSnap = await getDoc(inviteRef);

    if (!inviteSnap.exists()) {
      throw new Error('Invalid or expired invite code.');
    }

    const data = inviteSnap.data();

    if (Date.now() > data.expiryTime.toMillis()) {
      throw new Error('Invite code has expired.');
    }

    return {
      accessCode: data.accessCode,
      expiryTime: data.expiryTime,
      services: data.services || [],
      isAdmin: data.isAdmin || false,
      websiteId: data.websiteId
    };
  } catch (err) {
    console.error('Error fetching invite:', err);
    throw new Error("Error fetching invite.");
  }
};

export const getCurrentBusinessConfig = async (siteId: string = WEBSITE_ID): Promise<BusinessConfig> => {
  const db_website_ref: string = createBookingWebsiteDbRef(siteId);
  try {
    const configCollection = collection(db, db_website_ref, 'config');
    const configSnapshot = await getDocs(configCollection);

    // ✅ If the config collection is empty (i.e. doesn't exist), return an empty config
    if (configSnapshot.empty) {
      console.warn("Config collection does not exist. Returning empty config.");
      return createEmptyBusinessConfig();
    }

    const configData: BusinessConfig = createEmptyBusinessConfig();

    configSnapshot.forEach(docSnap => {
      const data = docSnap.data();

      switch (docSnap.id) {
        case 'businessInfo':
          configData.businessInfo = data as BusinessConfig['businessInfo'];
          break;
        case 'socials':
          configData.socials = data as BusinessConfig['socials'];
          break;
        case 'homepage':
          configData.homepage = data as BusinessConfig['homepage'];
          break;
        case 'features':
          configData.features = data as BusinessConfig['features'];
          break;
        case 'paymentInfo':
          configData.paymentInfo = data as BusinessConfig['paymentInfo'];
          break;
        case 'payoutInfo':
          configData.payoutInfo = data as BusinessConfig['payoutInfo'];
          break;
        case 'preferences':
          configData.preferences = data as BusinessConfig['preferences'];
          break;
        default:
          console.warn(`Unknown config entry: ${docSnap.id}`);
      }
    });

    // ✅ Ensure all pieces are filled or throw
    const requiredDocs = ['businessInfo', 'socials', 'homepage', 'features', 'payoutInfo'] as const;
    const missingDocs = requiredDocs.filter(requiredId => !configSnapshot.docs.some(doc => doc.id === requiredId));
    if (missingDocs.length > 0) {
      throw new Error("Incomplete business configuration data: Missing one or more required documents.");
    }

    if (configData.preferences?.orchestratorMode === true) {
      configData.payoutInfo = {
        ...(configData.payoutInfo ?? { useBusinessStripeAccount: false }),
        useBusinessStripeAccount: true,
      };
    }

    return configData;
  } catch (error) {
    console.error("Error fetching business configuration: ", error);
    throw new Error("Failed to fetch business configuration");
  }
};

export const getBusinessConfigById = async (siteId: string): Promise<BusinessConfig> => {
  try {
    const configCollection = collection(db, 'booking-websites', siteId, 'config');
    const configSnapshot = await getDocs(configCollection);

    const configData: BusinessConfig = createEmptyBusinessConfig();

    if (configSnapshot.empty) {
      console.warn(`No config docs found for site "${siteId}". Returning empty config.`);
      return configData;
    }

    configSnapshot.forEach((docSnap) => {
      const data = docSnap.data();
      switch (docSnap.id) {
        case 'businessInfo':
          configData.businessInfo = data as BusinessConfig['businessInfo'];
          break;
        case 'socials':
          configData.socials = data as BusinessConfig['socials'];
          break;
        case 'homepage':
          configData.homepage = data as BusinessConfig['homepage'];
          break;
        case 'features':
          configData.features = data as BusinessConfig['features'];
          break;
        case 'paymentInfo':
          configData.paymentInfo = data as BusinessConfig['paymentInfo'];
          break;
        case 'payoutInfo':
          configData.payoutInfo = data as BusinessConfig['payoutInfo'];
          break;
        case 'preferences':
          configData.preferences = data as BusinessConfig['preferences'];
          break;
        default:
          console.warn(`Unknown config section: ${docSnap.id}`);
      }
    });

    if (configData.preferences?.orchestratorMode === true) {
      configData.payoutInfo = {
        ...(configData.payoutInfo ?? { useBusinessStripeAccount: false }),
        useBusinessStripeAccount: true,
      };
    }

    return configData;
  } catch (error) {
    console.error(`Error fetching config for site "${siteId}":`, error);
    throw new Error("Failed to fetch business configuration");
  }
};

export const getPreviewSiteConfigByCode = async (code: string): Promise<PreviewSiteConfig | null> => {
  const previewSiteSnapshot = await getDoc(doc(db, 'preview-websites', code));

  if (!previewSiteSnapshot.exists()) {
    return null;
  }

  return {
    ...createEmptyPreviewSiteConfig(),
    ...(previewSiteSnapshot.data() as Partial<PreviewSiteConfig>),
  };
};

export const createPreviewSiteConfigByCode = async (code: string): Promise<void> => {
  const previewSiteRef = doc(db, 'preview-websites', code);

  await runTransaction(db, async (transaction) => {
    const previewSiteSnapshot = await transaction.get(previewSiteRef);
    if (previewSiteSnapshot.exists()) {
      throw new Error('A preview site with this code already exists.');
    }

    transaction.set(previewSiteRef, createEmptyPreviewSiteConfig());
  });
};

export const updatePreviewSiteConfig = async (
  code: string,
  config: PreviewSiteConfig,
  newBusinessLogoImage?: File
): Promise<void> => {
  const nextConfig: PreviewSiteConfig = { ...config };

  if (newBusinessLogoImage) {
    const businessLogoPath = nextConfig.businessLogoPath || `preview-websites/${code}/businessLogo`;
    await uploadImageToStorage(newBusinessLogoImage, businessLogoPath, 1);
    nextConfig.businessLogoPath = businessLogoPath;
  }

  await setDoc(doc(db, 'preview-websites', code), removeUndefinedFields(nextConfig), { merge: true });
  logEvent(analytics, 'updated_preview_site_config', { code });
};



export const updateBusinessConfig = async (
  updatedConfig: BusinessConfig,
  newHeroImage?: File,
  newLogoImage?: File,
  newHeroLogoImage?: File,
  siteId: string = WEBSITE_ID
): Promise<void> => {

  const homepage_db_ref: string = createBookingWebsiteDbRef(siteId, 'config/homepage');
  const businessInfo_db_ref: string = createBookingWebsiteDbRef(siteId, 'config/businessInfo');

  // Step 1: Handle hero image upload
  if (newHeroImage) {
    const heroImagePath = updatedConfig.homepage.heroImagePath || `${homepage_db_ref}/heroImage`;
    // Upload the new hero image at the same path

    await uploadImageToStorage(newHeroImage, heroImagePath, /*compressionLevel=*/1);
    // Ensure the updated config references the correct path

    updatedConfig.homepage.heroImagePath = heroImagePath;
  }

  // Step 1: Handle logo image upload
  if (newLogoImage) {
    const logoImagePath = updatedConfig.businessInfo.logoImagePath || `${businessInfo_db_ref}/logoImage`;
    // Upload the new logo image at the same path

    await uploadImageToStorage(newLogoImage, logoImagePath, /*compressionLevel=*/1);
    // Ensure the updated config references the correct path

    updatedConfig.businessInfo.logoImagePath = logoImagePath;
  }

  // Step 1: Handle hero logo image upload (for hero overlay placement)
  if (newHeroLogoImage) {
    const heroLogoImagePath = updatedConfig.homepage.heroLogoImagePath || `${homepage_db_ref}/heroLogoImage`;
    await uploadImageToStorage(newHeroLogoImage, heroLogoImagePath, /*compressionLevel=*/1);
    updatedConfig.homepage.heroLogoImagePath = heroLogoImagePath;
  }

  if (updatedConfig.preferences?.orchestratorMode === true) {
    updatedConfig.payoutInfo = {
      ...(updatedConfig.payoutInfo ?? { useBusinessStripeAccount: false }),
      useBusinessStripeAccount: true,
    };
  }

  // Reviews are managed in a dedicated screen. Avoid writing a potentially stale
  // reviews array when saving the broader business config.
  const { reviews: _ignoredReviews, ...homepageWithoutReviews } = updatedConfig.homepage;

  // Step 2: Update the business config documents in Firestore
  await Promise.all([
    setDoc(doc(db, 'booking-websites', siteId, 'config', 'businessInfo'), updatedConfig.businessInfo, { merge: true }),
    setDoc(doc(db, 'booking-websites', siteId, 'config', 'socials'), updatedConfig.socials, { merge: true }),
    setDoc(doc(db, 'booking-websites', siteId, 'config', 'homepage'), homepageWithoutReviews, { merge: true }),
    setDoc(doc(db, 'booking-websites', siteId, 'config', 'features'), updatedConfig.features, { merge: true }),
    setDoc(doc(db, 'booking-websites', siteId, 'config', 'paymentInfo'), updatedConfig.paymentInfo, { merge: true }),
    setDoc(doc(db, 'booking-websites', siteId, 'config', 'payoutInfo'), updatedConfig.payoutInfo, { merge: true }),
    setDoc(doc(db, 'booking-websites', siteId, 'config', 'preferences'), updatedConfig.preferences, { merge: true }),
  ]);

  // Step 3: Log the update event for analytics

  logEvent(analytics, 'updated_business_config', { siteId });
};

export const updateHomepageConfig = async (
  homepage: BusinessConfig['homepage'],
  siteId: string = WEBSITE_ID
): Promise<void> => {
  await setDoc(doc(db, 'booking-websites', siteId, 'config', 'homepage'), homepage, { merge: true });
  logEvent(analytics, 'updated_business_config_homepage', { siteId });
};

export const updateHomepageReviews = async (
  reviews: BusinessConfig['homepage']['reviews'],
  siteId: string = WEBSITE_ID
): Promise<void> => {
  await setDoc(
    doc(db, 'booking-websites', siteId, 'config', 'homepage'),
    { reviews: reviews ?? [] },
    { merge: true }
  );
  logEvent(analytics, 'updated_business_config_homepage_reviews', { siteId });
};

export const updatePreferencesConfig = async (
  preferences: BusinessConfig['preferences'],
  siteId: string = WEBSITE_ID
): Promise<void> => {
  await setDoc(doc(db, 'booking-websites', siteId, 'config', 'preferences'), preferences, { merge: true });
  logEvent(analytics, 'updated_business_config_preferences', { siteId });
};

export const patchPreferencesConfig = async (
  preferencesPatch: Partial<BusinessConfig['preferences']>,
  siteId: string = WEBSITE_ID
): Promise<void> => {
  await setDoc(doc(db, 'booking-websites', siteId, 'config', 'preferences'), preferencesPatch, { merge: true });
  logEvent(analytics, 'patched_business_config_preferences', { siteId });
};

/**
 * True if:
 *  - employee has `employeeStripeAccounts/{employeeId}.stripe_account_id`, OR
 *  - business config `payoutInfo` has `useBusinessStripeAccount` + `businessStripeAccountId`
 */
export const canAcceptCardForBooking = async (
  employeeId: string,
  siteId: string = WEBSITE_ID,
  businessConfigOverride?: BusinessConfig | null
): Promise<boolean> => {
  const db_website_ref = createBookingWebsiteDbRef(siteId);

  const configPromise = businessConfigOverride
    ? Promise.resolve(businessConfigOverride)
    : getCurrentBusinessConfig(siteId).catch((err) => {
      console.warn('getCurrentBusinessConfig failed (continuing as empty):', err);
      return createEmptyBusinessConfig();
    });

  const [config, empSnap] = await Promise.all([
    configPromise,
    getDoc(doc(db, db_website_ref, 'employeeStripeAccounts', employeeId)),
  ]);

  const payout = (config as BusinessConfig).payoutInfo;
  const bizStripeEnabled = !!(payout?.useBusinessStripeAccount && payout?.businessStripeAccountId);

  const empHasStripe = empSnap.exists() && !!(empSnap.data() as any)?.stripe_account_id;

  return empHasStripe || bizStripeEnabled;
};


const chunk = <T,>(arr: T[], size: number) => {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

export const fetchAllAppointmentsForSite = async (siteId: string = WEBSITE_ID): Promise<Appointment[]> => {
  const db_website_ref: string = createBookingWebsiteDbRef(siteId);
  const appointmentsRef = collection(db, db_website_ref, 'appointments');
  const pageSize = 400;
  const appointments: Appointment[] = [];
  let lastDoc: any | null = null;

  while (true) {
    const constraints = lastDoc
      ? [orderBy(documentId()), startAfter(lastDoc), limit(pageSize)]
      : [orderBy(documentId()), limit(pageSize)];

    const snapshot = await getDocs(query(appointmentsRef, ...constraints));
    snapshot.forEach((docSnap) => {
      const data = docSnap.data() as Omit<Appointment, 'id'>;
      const appointment = { id: docSnap.id, ...data };
      if (isRealAppointment(appointment)) {
        appointments.push(appointment);
      }
    });

    if (snapshot.size < pageSize) {
      break;
    }
    lastDoc = snapshot.docs[snapshot.docs.length - 1];
  }

  return appointments;
};

export const fetchEmployeeStatsDailyRange = async (
  employeeId: string,
  startKey: string,
  endKey: string,
  siteId: string = WEBSITE_ID
): Promise<Record<string, StatsDocNumbers>> => {
  const db_website_ref: string = createBookingWebsiteDbRef(siteId);
  const statsRef = collection(db, db_website_ref, 'employees', employeeId, 'statsDaily');
  const statsQuery = query(statsRef, orderBy(documentId()), startAt(startKey), endAt(endKey));
  const snapshot = await getDocs(statsQuery);
  const map: Record<string, StatsDocNumbers> = {};
  snapshot.forEach((docSnap) => {
    map[docSnap.id] = normalizeStatsNumbers(docSnap.data() as Partial<StatsDocNumbers>);
  });
  return map;
};

export const fetchEmployeeStatsMonthlyForYear = async (
  employeeId: string,
  year: number,
  siteId: string = WEBSITE_ID
): Promise<Record<string, StatsDocNumbers>> => {
  const db_website_ref: string = createBookingWebsiteDbRef(siteId);
  const statsRef = collection(db, db_website_ref, 'employees', employeeId, 'statsMonthly');
  const startKey = `${year}01`;
  const endKey = `${year}12`;
  const monthlyQuery = query(statsRef, orderBy(documentId()), startAt(startKey), endAt(endKey));
  const monthlySnapshot = await getDocs(monthlyQuery);
  const monthlyMap: Record<string, StatsDocNumbers> = {};

  monthlySnapshot.forEach((docSnap) => {
    monthlyMap[docSnap.id] = normalizeStatsNumbers(docSnap.data() as Partial<StatsDocNumbers>);
  });

  if (!monthlySnapshot.empty) {
    return monthlyMap;
  }

  // Fallback to summing daily docs for the year if no monthly stats exist yet.
  const dailyRef = collection(db, db_website_ref, 'employees', employeeId, 'statsDaily');
  const dailyQuery = query(
    dailyRef,
    orderBy(documentId()),
    startAt(`${year}0101`),
    endAt(`${year}1231`)
  );
  const dailySnapshot = await getDocs(dailyQuery);
  dailySnapshot.forEach((docSnap) => {
    const numbers = normalizeStatsNumbers(docSnap.data() as Partial<StatsDocNumbers>);
    const monthKey = toYyyyMmFromYyyyMmDd(docSnap.id);
    if (!monthKey) return;
    const existing = monthlyMap[monthKey] ?? (monthlyMap[monthKey] = emptyStatsAccumulator());
    mergeStatsNumbers(existing, numbers);
  });

  return monthlyMap;
};

export const fetchBusinessStatsDailyRange = async (
  startKey: string,
  endKey: string,
  siteId: string = WEBSITE_ID
): Promise<Record<string, StatsDocNumbers>> => {
  const db_website_ref: string = createBookingWebsiteDbRef(siteId);
  const statsRef = collection(db, db_website_ref, 'statsDaily');
  const statsQuery = query(statsRef, orderBy(documentId()), startAt(startKey), endAt(endKey));
  const snapshot = await getDocs(statsQuery);
  const map: Record<string, StatsDocNumbers> = {};
  snapshot.forEach((docSnap) => {
    map[docSnap.id] = normalizeStatsNumbers(docSnap.data() as Partial<StatsDocNumbers>);
  });
  return map;
};

export const fetchBusinessStatsMonthlyForYear = async (
  year: number,
  siteId: string = WEBSITE_ID
): Promise<Record<string, StatsDocNumbers>> => {
  const db_website_ref: string = createBookingWebsiteDbRef(siteId);
  const statsRef = collection(db, db_website_ref, 'statsMonthly');
  const startKey = `${year}01`;
  const endKey = `${year}12`;
  const monthlyQuery = query(statsRef, orderBy(documentId()), startAt(startKey), endAt(endKey));
  const monthlySnapshot = await getDocs(monthlyQuery);
  const monthlyMap: Record<string, StatsDocNumbers> = {};

  monthlySnapshot.forEach((docSnap) => {
    monthlyMap[docSnap.id] = normalizeStatsNumbers(docSnap.data() as Partial<StatsDocNumbers>);
  });

  return monthlyMap;
};

const deleteCollectionPaginated = async (colRef: ReturnType<typeof collection>, batchSize: number = 400) => {
  let cursor: any | null = null;
  while (true) {
    const pageQuery = cursor
      ? query(colRef, orderBy(documentId()), startAfter(cursor), limit(batchSize))
      : query(colRef, orderBy(documentId()), limit(batchSize));
    const snap = await getDocs(pageQuery);
    if (snap.empty) break;
    const batch = writeBatch(db);
    snap.docs.forEach((docSnap) => batch.delete(docSnap.ref));
    await batch.commit();
    if (snap.size < batchSize) break;
    cursor = snap.docs[snap.docs.length - 1];
  }
};

const clearStatsCollectionsForSite = async (siteId: string) => {
  const db_website_ref: string = createBookingWebsiteDbRef(siteId);
  const statsDailyRef = collection(db, db_website_ref, 'statsDaily');
  const statsMonthlyRef = collection(db, db_website_ref, 'statsMonthly');

  await Promise.all([
    deleteCollectionPaginated(statsDailyRef),
    deleteCollectionPaginated(statsMonthlyRef),
  ]);

  // Clear employee stats collections
  const employeesSnap = await getDocs(collection(db, db_website_ref, 'employees'));
  for (const empDoc of employeesSnap.docs) {
    const empDailyRef = collection(db, db_website_ref, 'employees', empDoc.id, 'statsDaily');
    const empMonthlyRef = collection(db, db_website_ref, 'employees', empDoc.id, 'statsMonthly');
    await deleteCollectionPaginated(empDailyRef);
    await deleteCollectionPaginated(empMonthlyRef);
  }
};


export const rebuildStatsForSite = async (siteId: string = WEBSITE_ID): Promise<void> => {
  const appointments = await fetchAllAppointmentsForSite(siteId);
  const { businessDailyMap, businessMonthlyMap, employeeDailyMap, employeeMonthlyMap } =
    buildStatsMapsFromAppointments(appointments);

  const db_website_ref: string = createBookingWebsiteDbRef(siteId);
  const writes: StatsWrite[] = [];
  const wrapDoc = (key: string, data: StatsDocNumbers): StatsWrite => ({
    ref: doc(db, db_website_ref, 'statsDaily', key),
    data: { key, updatedAt: serverTimestamp(), ...data },
  });

  await clearStatsCollectionsForSite(siteId);

  Object.entries(businessDailyMap).forEach(([key, data]) => {
    writes.push(wrapDoc(key, data));
  });
  Object.entries(businessMonthlyMap).forEach(([key, data]) => {
    writes.push({
      ref: doc(db, db_website_ref, 'statsMonthly', key),
      data: { key, updatedAt: serverTimestamp(), ...data },
    });
  });

  Object.entries(employeeDailyMap).forEach(([employeeId, dailyMap]) => {
    Object.entries(dailyMap).forEach(([key, data]) => {
      writes.push({
        ref: doc(db, db_website_ref, 'employees', employeeId, 'statsDaily', key),
        data: { key, updatedAt: serverTimestamp(), ...data },
      });
    });
  });
  Object.entries(employeeMonthlyMap).forEach(([employeeId, monthlyMap]) => {
    Object.entries(monthlyMap).forEach(([key, data]) => {
      writes.push({
        ref: doc(db, db_website_ref, 'employees', employeeId, 'statsMonthly', key),
        data: { key, updatedAt: serverTimestamp(), ...data },
      });
    });
  });

  const batches = chunk(writes, 400);
  for (const batchWrites of batches) {
    const batch = writeBatch(db);
    batchWrites.forEach(({ ref, data }) => {
      batch.set(ref, data, { merge: true });
    });
    await batch.commit();
  }
};

export const rebuildCustomersForSite = async (siteId: string = WEBSITE_ID): Promise<void> => {
  const appointments = await fetchAllAppointmentsForSite(siteId);
  const customersByPhone = new Map<string, {
    phone: string;
    firstName: string;
    lastName: string;
    email: string;
    totalAppointments: number;
    totalCompletedRevenue: number;
    lastAppointmentDate: Timestamp | null;
    lastAppointmentSort: Dayjs | null;
    employeeIds: Set<string>;
  }>();

  appointments.forEach((appointment) => {
    if (appointment.status !== AppointmentStatus.COMPLETE) {
      return;
    }
    const phone = (appointment.customerPhoneNumber ?? '').trim();
    if (!phone) {
      return;
    }
    const existing = customersByPhone.get(phone) ?? {
      phone,
      firstName: '',
      lastName: '',
      email: '',
      totalAppointments: 0,
      totalCompletedRevenue: 0,
      lastAppointmentDate: null,
      lastAppointmentSort: null,
      employeeIds: new Set<string>(),
    };

    existing.totalAppointments += 1;

    existing.totalCompletedRevenue += Number(appointment.billingAmount ?? 0);

    if (appointment.employeeID) {
      existing.employeeIds.add(appointment.employeeID);
    }

    let appointmentMoment: Dayjs | null = null;
    if (appointment.appointmentDate) {
      const combined = `${appointment.appointmentDate}${appointment.appointmentTime ?? ''}`;
      const combinedMoment = dayjs(combined, 'MMDDYYYYHHmm');
      appointmentMoment = combinedMoment.isValid()
        ? combinedMoment
        : dayjs(appointment.appointmentDate, 'MMDDYYYY');
    }
    if ((!appointmentMoment || !appointmentMoment.isValid()) && appointment.creationTime) {
      appointmentMoment = dayjs(appointment.creationTime.toDate());
    }
    const isValidMoment = appointmentMoment && appointmentMoment.isValid();

    if (!existing.lastAppointmentSort || (isValidMoment && appointmentMoment!.isAfter(existing.lastAppointmentSort))) {
      existing.lastAppointmentSort = isValidMoment ? appointmentMoment! : existing.lastAppointmentSort;
      if (isValidMoment) {
        existing.lastAppointmentDate = Timestamp.fromDate(appointmentMoment!.toDate());
      }
      existing.firstName = appointment.customerFirstName ?? '';
      existing.lastName = appointment.customerLastName ?? '';
      existing.email = appointment.customerEmail ?? '';
    }

    customersByPhone.set(phone, existing);
  });

  const db_website_ref: string = createBookingWebsiteDbRef(siteId);
  const writes: Array<{ ref: any; data: Omit<Customer, 'id'> }> = [];

  customersByPhone.forEach((customer) => {
    const customerRef = doc(collection(db, db_website_ref, 'customers'), customer.phone);
    writes.push({
      ref: customerRef,
      data: {
        firstName: customer.firstName,
        lastName: customer.lastName,
        email: customer.email,
        phone: customer.phone,
        totalAppointments: customer.totalAppointments,
        totalCompletedRevenue: customer.totalCompletedRevenue,
        lastAppointmentDate: customer.lastAppointmentDate,
        employeeIds: Array.from(customer.employeeIds.values()),
      },
    });
  });

  const batches = chunk(writes, 400);
  for (const batchWrites of batches) {
    const batch = writeBatch(db);
    batchWrites.forEach(({ ref, data }) => {
      batch.set(ref, data, { merge: true });
    });
    await batch.commit();
  }
};
